import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button,Timeline, Card, Col, Empty, Progress, Row, Skeleton, Space, Table, Tabs, Tag,Typography,Alert, List, Form, Input, Select, DatePicker, Modal} from "antd";
import {
  CheckCircleFilled,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined, ExclamationCircleOutlined,
  EditOutlined,
  FolderOpenOutlined,
  LeftOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { useForm } from "antd/es/form/Form"; // Correct import for useForm
import { useDispatch, useSelector } from "react-redux"; // Assuming useDispatch is from react-redux
import { Client } from "../../core/api-client";
import {
  API_COMMON_GET_BY_MODULE,    
  API_GET_ALL_DEPARTMENT_LIST,API_GET_ALL_PROJECT_LIST,API_GET_ALL_STAFF_LIST,API_GET_ALL_VENDOR_LIST, API_GET_SUBDEPARTMENTS_BY_DEPARTMENT, API_GET_ACCOUNT_LIST_BY_ENTITY,
  API_GET_ACTIVITY_LOG, API_UPDATE_STAFF,
  API_GET_STAFF_DETAILS, API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST, API_GET_DOCUMENTS, API_GET_ALL_DOCUMENT_SUB_TYPE_LIST, API_DELETE_DOCUMENT, API_GET_DOCUMENT_SIGNED_URL,
  ROUTE_MANAGE_STAFF,
} from "../../shared/routes/private.routes";
import { formatDate, showNotification } from "../../shared/utils/utils";
import { DocumentType } from "../../shared/types/common";

import CreateEditStaffComponent from "./components/create-edit-staff.component";
import { defaultStaffProfileMock } from "./data/staff-profile.mock";
import { NATIONALITY_OPTIONS } from "./data/nationality-options";
import { COUNTRY_CODES } from "./data/Country-options";
import {
  StaffProfileDocumentCategory,
  StaffProfileDependent,
  StaffProfileField,
  StaffProfileMetric,
  StaffProfilePositionHistory,
  StaffProfileTabKey,
} from "./types/staff-profile.types"; // Ensure this path is correct
import "./staff-profile.less"; // Assuming this is the correct path for the less file
import { accessAllowed } from "../../shared/utils/utils";
import { AppDispatch,RootState } from "../../core/store"; // Assuming AppDispatch is exported from your store configuration
import { updateStaff } from "../../core/store/reducers/staff.slice";
import UploadDocumentModal from "./components/upload-document-modal.component";
import { NavigationBlockerContext } from "../../App";
import { allPermissions } from "../../shared/constants/constants";

const { TabPane } = Tabs;

// Tabs commented out below are hidden until the system has real data for them.
// Their render functions and `renderTab` cases are left intact - uncomment the
// line here to bring a tab back, nothing else needs to change.
const profileTabs: { key: StaffProfileTabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "personal", label: "Personal" },
  { key: "job", label: "Job Details" },
  // { key: "onboarding", label: "Onboarding" },
  { key: "documents", label: "Documents" },
  // { key: "letters", label: "Letters" },
  // { key: "compensation", label: "Compensation" },
  // { key: "assets", label: "Assets" },
  // { key: "leave", label: "Leave & Attendance" },
  // { key: "performance", label: "Performance" },
  { key: "timeline", label: "Timeline" },
  { key: "exit", label: "Exit" },
];

const emptyStaff = {
  staffName: "",
  employeeId: "",
  staffStatusId: "",
};

// Kept in sync with create-edit-staff.component.tsx so the inline profile edit
// enforces exactly the same rules as the create/edit drawer.
const NOTICE_PERIOD_STATUS = "NOTICE PERIOD";
const DEFAULT_STAFF_EMAIL_DOMAIN = "@roboticodigital.com";
const INNOTHRUST_EMAIL_DOMAIN = "@innothrust.com";
const INNOTHRUST_ENTITY_NAME = "innothrust";
const VENDOR_CONSULTANT_MODE = "Vendor Consultant";
const PERMANENT_EMPLOYMENT_MODE = "Permanent";

const textOnlyRule = (fieldName: string) => ({
  pattern: /^[A-Za-z\s.'/-]+$/,
  message: `Please enter a valid ${fieldName}. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed.`,
});

const numberOnlyRule = (fieldName: string) => ({
  pattern: /^\d+$/,
  message: `Please enter a valid ${fieldName}. Only numbers are allowed.`,
});

const valueOrFallback = (value: any, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const dateOrFallback = (value: any, fallback = "-") => {
  if (!value || !moment(value).isValid()) return fallback;
  return formatDate(value, "DD-MMM-YYYY");
};

const firstRealValue = (...values: any[]) =>
  values.find((value) => value !== null && value !== undefined && value !== "");

const isUuidLike = (value: any) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );

const formatDays = (value: any) => {
  const realValue = firstRealValue(value);
  return realValue ? `${realValue} days` : "-";
};

const parseDependents = (value: any): StaffProfileDependent[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parsePositionHistory = (value: any): StaffProfilePositionHistory[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDependents = (dependents: any[] = []) =>
  dependents
    .filter((dependent) =>
      ["name", "relationship", "dateOfBirth", "coveredInInsurance"].some(
        (key) => dependent?.[key]
      )
    )
    .map((dependent) => ({
      name: dependent?.name || "",
      relationship: dependent?.relationship || "",
      dateOfBirth: dependent?.dateOfBirth && dayjs(dependent.dateOfBirth).isValid() ? dayjs(dependent.dateOfBirth) : null,
      coveredInInsurance: dependent?.coveredInInsurance || "No",
    }));

const normalizePositionHistory = (history: any[] = []) =>
  history
    .filter((item) =>
      ["effectiveFrom", "designation", "department", "reportingTo"].some(
        (key) => item?.[key]
      )
    )
    .map((item) => ({
      effectiveFrom:
        item?.effectiveFrom && typeof item.effectiveFrom?.format === "function"
          ? item.effectiveFrom.format("YYYY-MM-DD")
          : item?.effectiveFrom && dayjs(item.effectiveFrom).isValid()
            ? dayjs(item.effectiveFrom).format("YYYY-MM-DD")
            : item?.effectiveFrom || "",
      designation: item?.designation || "",
      department: item?.department || "",
      reportingTo: item?.reportingTo || "",
    }));

const normalizeComparableValue = (value: any, type: string) => {
  if (type === "date") {
    return value ? dayjs(value).format("YYYY-MM-DD") : null;
  }
  if (type === "select") {
    return value === undefined ? null : value;
  }
  if (type === "json") {
    return JSON.stringify(normalizeDependents(parseDependents(value)));
  }
  if (type === "positionHistory") {
    return JSON.stringify(normalizePositionHistory(parsePositionHistory(value)));
  }
  return value === null || value === undefined || value === "" ? null : String(value);
};

const normalizeDatePayloadValue = (value: any) => {
  if (!value) return null;
  if (typeof value?.format === "function") return value.format("YYYY-MM-DD");
  return dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : value;
};

const formatAadhaar = (value: string) => {
  if (!value) return "";
  const digitsOnly = value.replace(/\D/g, "");
  return digitsOnly
    .slice(0, 12)
    .replace(/(\d{4})/g, "$1 ")
    .trim();
};

const unformatAadhaar = (value: string) => {
  return value ? value.replace(/\s/g, "") : "";
};
const formatDependentsSummary = (dependents: any[]) => {
  const normalized = normalizeDependents(dependents);
  if (normalized.length === 0) return "-";
  return normalized
    .map((dependent) =>
      [dependent.name, dependent.relationship].filter(Boolean).join(" - ")
    )
    .join(", ");
};

const formatPositionHistorySummary = (history: any[]) => {
  const normalized = normalizePositionHistory(history);
  if (normalized.length === 0) return "-";
  return normalized
    .map((item) =>
      [item.effectiveFrom, item.designation].filter(Boolean).join(" - ")
    )
    .join(", ");
};

const toPositionHistoryFormValues = (history: any[] = []) =>
  normalizePositionHistory(history).map((item) => ({
    ...item,
    effectiveFrom: item.effectiveFrom && dayjs(item.effectiveFrom).isValid()
      ? dayjs(item.effectiveFrom)
      : null,
  }));

 

const formatMoney = (amount: any, currency: any) => {
  const realAmount = firstRealValue(amount);
  if (!realAmount) return "-";
  const currencyText =
    typeof currency === "object"
      ? firstRealValue(currency?.name, currency?.code, currency?.id)
      : currency;
  return currencyText ? `${currencyText} ${realAmount}` : `${realAmount}`;
};

const calculateTenure = (doj: any, lwd?: any) => {
  if (!doj || !moment(doj).isValid()) return "-";
  const start = moment(doj);
  const end = lwd && moment(lwd).isValid() ? moment(lwd) : moment();
  const years = end.diff(start, "years");
  start.add(years, "years");
  const months = end.diff(start, "months");
  const yearText = years ? `${years} yr${years > 1 ? "s" : ""}` : "";
  const monthText = months ? `${months} mo${months > 1 ? "s" : ""}` : "";
  return [yearText, monthText].filter(Boolean).join(" ") || "0 mo";
};

/** Current Indian financial year (April - March), e.g. "FY 2026-27". */
const getCurrentFinancialYear = () => {
  const now = moment();
  const startYear = now.month() >= 3 ? now.year() : now.year() - 1;
  return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
};

// const COUNTRY_CODES = [
//   { label: "IN (+91)", value: "+91", pattern: /^\d{10}$/ },
//   { label: "US (+1)", value: "+1", pattern: /^\d{10}$/ },
//   { label: "UK (+44)", value: "+44", pattern: /^\d{10}$/ },
//   { label: "AU (+61)", value: "+61", pattern: /^\d{9}$/ },
//   { label: "MY (+60)", value: "+60", pattern: /^\d{9,10}$/ },
//   { label: "SG (+65)", value: "+65", pattern: /^\d{8}$/ },
// ];



const resolveMasterName = (value: any, masterList: any[]) => {
  const realValue = firstRealValue(value);
  if (!realValue) return "-";
  const matched = masterList.find(
    (item) => item?.id === realValue || item?.value === realValue
  );
  return matched?.name || matched?.label || (isUuidLike(realValue) ? "-" : realValue);
};

const isJson = (value: any) => {
  if (!value || typeof value !== "string") return false;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const formatActivityDate = (date: any, fallback = "-") => {
  if (!date || !moment(date).isValid()) return fallback;
  return moment.utc(date).local().format("DD-MMM-YYYY");
};

const getActivityActor = (entry: any) =>
  entry?.updatedUserDetails?.[0]?.fullName || entry?.updatedByName || "System";

const isBlankActivityValue = (value: any) =>
  value === null || value === undefined || value === "";

const shouldSkipStaffNullRemoval = (subject: string, change: any) =>
  subject.includes("Update Staff") &&
  !isBlankActivityValue(change?.oldValue) &&
  isBlankActivityValue(change?.newValue);

const buildActivityEntries = (logs: any[], staffRecord: any) => {
  const entries: { title: string; date: string; description: string }[] = [];

  logs.forEach((entry) => {
    const subject = entry?.subject || "Staff activity";
    const actor = getActivityActor(entry);
    const date = formatActivityDate(entry?.created_date || entry?.createdDate);

    if (subject.includes("Update") && isJson(entry?.newValue)) {
      const changes = JSON.parse(entry.newValue);
      if (Array.isArray(changes)) {
        changes.forEach((change) => {
          if (change?.oldValue === change?.newValue) return;
          if (shouldSkipStaffNullRemoval(subject, change)) return;
          const fieldName = change?.fieldName || change?.fieldCode || "Field";
          const oldValue = valueOrFallback(change?.oldValue, "blank");
          const newValue = valueOrFallback(change?.newValue, "blank");
          entries.push({
            title: `${fieldName} updated`,
            date,
            description: `${actor} changed ${fieldName} from ${oldValue} to ${newValue}.`,
          });
        });
        return;
      }
    }

    const oldValue = firstRealValue(entry?.oldvalue, entry?.oldValue);
    const newValue = firstRealValue(entry?.newvalue, entry?.newValue);
    const description =
      oldValue || newValue
        ? `${actor} changed ${valueOrFallback(oldValue, "blank")} to ${valueOrFallback(newValue, "blank")}.`
        : `by ${actor}`;

    entries.push({
      title: subject,
      date,
      description,
    });
  });

  const hasCreateLog = entries.some((entry) =>
    entry.title.toLowerCase().includes("created")
  );

  if (!hasCreateLog && staffRecord?.createdDate) {
    entries.push({
      title: "Staff Created",
      date: formatActivityDate(staffRecord.createdDate),
      description: "Initial staff profile created.",
    });
  }

  return entries.sort((a, b) => moment(b.date, "DD-MMM-YYYY").valueOf() - moment(a.date, "DD-MMM-YYYY").valueOf());
};

const initialsFor = (name: string) =>
  (name || "Staff Member")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const withRealValues = (
  fields: StaffProfileField[],
  replacements: Record<string, string>
) =>
  fields.map((field) => ({
    ...field,
    value: replacements[field.label] || field.value,
  }));

const FieldGrid = ({ fields }: { fields: StaffProfileField[] }) => (
  <div className="staff-profile-field-grid">
    {fields.map((field) => (
      <div className="staff-profile-field" key={field.label}>
        <span>{field.label}</span>
        <strong>{valueOrFallback(field.value)}</strong>
      </div>
    ))}
  </div>
);

// const Section = ({
//   title,
//   children,
//   className = "",
// }: {
//   title: string;
//   children: React.ReactNode;
//   className?: string;
// }) => (
//   <div className={`staff-profile-section ${className}`}>
//     <h3>{title}</h3>
//     {children}
//   </div>
// );

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  onEdit?: () => void;
  isEditing?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  canEdit?: boolean;
  loading?: boolean;
}> = ({
  title,
  children,
  className = "",
  onEdit,
  isEditing = false,
  onSave,
  onCancel,
  canEdit = true,
  loading = false,
}) => (
  <div className={`staff-profile-section ${className}`}>
    <div className="staff-profile-section-header">
      <h3>{title}</h3>

      {canEdit && onEdit && !isEditing && (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={onEdit}
        />
      )}

      {isEditing && (
        <Space>
          <Button
            size="small"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            size="small"
            type="primary"
            onClick={onSave}
            loading={loading}
          >
            Save
          </Button>
        </Space>
      )}
    </div>

    {children}
  </div>
);


const PlaceholderSection = ({ title, fields }: { title: string; fields: StaffProfileField[] }) => (
  <Section title={title}>
    <FieldGrid fields={fields} />
  </Section>
);

const StaffProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routedStaff = (location.state as any)?.staff; // Moved declaration here
  const { staffId } = useParams();
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [activeTab, setActiveTab] = useState<StaffProfileTabKey>("overview");
  const [tabLoading, setTabLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { setBlocker } = React.useContext(NavigationBlockerContext);
  const [isEditing, setIsEditing] = useState(false);
  const [tempFormData, setTempFormData] = useState<any>({});
  const [pendingTab, setPendingTab] = useState<StaffProfileTabKey | null>(null); // This will store the tab key for pending navigation
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [form] = useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changesToConfirm, setChangesToConfirm] = useState<any[]>([]);
  const [activeDocumentCategoryId, setActiveDocumentCategoryId] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [staffDocuments, setStaffDocuments] = useState<any[]>([]);
  const [documentCounts, setDocumentCounts] = useState<Record<string, number>>({});
  const [documentActionLoading, setDocumentActionLoading] = useState<string | null>(null);
  const [staff, setStaff] = useState<any>(routedStaff || emptyStaff);
  const [loading, setLoading] = useState(Boolean(staffId));
  const [editOpen, setEditOpen] = useState(false);
  const [employeeTypes, setEmployeeTypes] = useState<any[]>([]);
  const [employmentModes, setEmploymentModes] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [lineManagers, setLineManagers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<any[]>([]);
  const [bloodGroups, setBloodGroups] = useState<any[]>([]);
  const [loadingStaffDocuments, setLoadingStaffDocuments] = useState(false);
 // const [staffList, setStaffList] = useState<any[]>([]);
 // const [projectList, setProjectList] = useState<any[]>([]);
  const [genderList, setGenderList] = useState<any[]>([]);
  // const [currencies, setCurrencies] = useState<any[]>([]);
//  const [departmentList, setDepartmentList] = useState<any[]>([]);   
  const [entityList, setEntityList] = useState<any[]>([]);
  const [vendorList, setVendorList] = useState<any[]>([]);
  const [subDepartmentList, setSubDepartmentList] = useState<any[]>([]);
  const [accountList, setAccountList] = useState<any[]>([]);
  const [filteredProjectList, setFilteredProjectList] = useState<any[]>([]);
  const [activityLogData, setActivityLogData] = useState<any[]>([]); // Keep this as any[] or define a specific type for activity logs
  const dispatch = useDispatch<AppDispatch>(); // Type dispatch with AppDispatch
  const [editingSection, setEditingSection] = useState<string | null>(null);
  // Conditional field visibility, mirroring the create/edit staff drawer.
  const [showVendorField, setShowVendorField] = useState(false);
  const [showContractFields, setShowContractFields] = useState(false);
  const [isNoticePeriodStatus, setIsNoticePeriodStatus] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const tabLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canUpdateStaff = useMemo(() => accessAllowed(myPermissions, allPermissions.UPDATE_STAFF), [myPermissions]); // Corrected accessAllowed usage

  const textOnlyRule = (fieldName: string) => ({
    pattern: /^[A-Za-z\s.'/-]+$/,
    message: `Please enter a valid ${fieldName}. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed.`,
  });

  const numberOnlyRule = (fieldName: string) => ({
    pattern: /^\d+$/,
    message: `Please enter a valid ${fieldName}. Only numbers are allowed.`,
  });

  const getJobDetailRules = (fieldKey: string) => {
    const isContractEmploymentMode = () => {
      const selectedEmploymentMode = form.getFieldValue("employmentMode");
      const permanentEmploymentMode = employmentModes.find((type: any) => type.name === "Permanent");
      return !!selectedEmploymentMode && selectedEmploymentMode !== permanentEmploymentMode?.id;
    };

    switch (fieldKey) {
      case "staffName":
        return [
          { required: true, message: "Please enter Staff Name" },
          textOnlyRule("Staff Name"),
        ];
      case "entity":
        return [{ required: true, message: "Please select Entity" }];
      case "employeeId":
        return [
          { required: true, message: "Please enter Employee Id" },
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const regex = /^[A-Za-z]{2,3}[0-9]{5}$/;
              return regex.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Invalid format. Please check the Info."));
            },
          },
        ];
      case "employmentMode":
        return [{ required: true, message: "Please select Employment Mode" }];
      case "employeeType":
        return [{ required: true, message: "Please enter Employee Type" }];
      case "designation":
        return [{ required: false, message: "Please enter Designation" }, textOnlyRule("Designation")];
      case "role":
        return [{ required: false, message: "Please enter Role" }, textOnlyRule("Role")];
      case "doj":
        return [{ required: true, message: "Please enter Date of Joining" }];
      case "contractStartDate":
        return [
          {
            required: isContractEmploymentMode(),
            message: "Please enter Contract Start Date",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const dateOfJoining = getFieldValue("doj");
              const contractEndDate = getFieldValue("contractEndDate");

              if (isContractEmploymentMode() && !value) {
                return Promise.reject(new Error("Please enter Contract Start Date"));
              }

              if (value && dateOfJoining && dayjs(value).isBefore(dayjs(dateOfJoining), "day")) {
                return Promise.reject(
                  new Error("Contract Start Date cannot be earlier than Date of Joining")
                );
              }

              if (value && contractEndDate && dayjs(value).isAfter(dayjs(contractEndDate), "day")) {
                return Promise.reject(
                  new Error("Contract Start Date cannot be later than Contract End Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "contractEndDate":
        return [
          {
            required: isContractEmploymentMode(),
            message: "Please enter Contract End Date",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const contractStartDate = getFieldValue("contractStartDate");
              const lastWorkingDay = getFieldValue("lwd");

              if (isContractEmploymentMode() && !value) {
                return Promise.reject(new Error("Please enter Contract End Date"));
              }

              if (value && contractStartDate && dayjs(value).isBefore(dayjs(contractStartDate), "day")) {
                return Promise.reject(
                  new Error("Contract End Date cannot be earlier than Contract Start Date")
                );
              }

              if (value && lastWorkingDay && dayjs(value).isAfter(dayjs(lastWorkingDay), "day")) {
                return Promise.reject(
                  new Error("Contract End Date cannot be later than Last Working Day")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "lwd":
        return [
          {
            required: form.getFieldValue("staffStatusId") === NOTICE_PERIOD_STATUS,
            message: "Please enter Last Working Day",
          },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const staffStatus = getFieldValue("staffStatusId");
              const startDate = getFieldValue("doj");
              const contractEndDate = getFieldValue("contractEndDate");

              if (staffStatus === NOTICE_PERIOD_STATUS && !value) {
                return Promise.reject(new Error("Please enter Last Working Day"));
              }

              if (value && startDate && dayjs(value).isBefore(dayjs(startDate), "day")) {
                return Promise.reject(
                  new Error("Last Working Day cannot be earlier than Date of Joining")
                );
              }

              if (value && contractEndDate && dayjs(value).isBefore(dayjs(contractEndDate), "day")) {
                return Promise.reject(
                  new Error("Last Working Day cannot be earlier than Contract End Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "departmentId":
        return [{ required: true, message: "Please enter Department" }];
      case "subDepartmentId":
        return [{ required: false, message: "Please select Sub Department" }];
      case "lineManagerId":
        return [{ message: "Please enter Line Manager" }];
      case "noticePeriod":
        return [{ required: false, message: "Please enter Notice Period" }, numberOnlyRule("Notice Period")];
      case "previousSalaryRevisionDate":
        return [
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const startDate = getFieldValue("doj");
              const nextSalaryRevisionDate = getFieldValue("nextSalaryRevisionDate");

              if (value && startDate && dayjs(value).isBefore(dayjs(startDate), "day")) {
                return Promise.reject(
                  new Error("Previous Salary Revision Date cannot be earlier than Date of Joining")
                );
              }

              if (value && nextSalaryRevisionDate && dayjs(value).isAfter(dayjs(nextSalaryRevisionDate), "day")) {
                return Promise.reject(
                  new Error("Previous Salary Revision Date cannot be later than Next Salary Revision Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      case "nextSalaryRevisionDate":
        return [
          ({ getFieldValue }: any) => ({
            validator(_: any, value: any) {
              const previousSalaryRevisionDate = getFieldValue("previousSalaryRevisionDate");

              if (value && previousSalaryRevisionDate && dayjs(value).isBefore(dayjs(previousSalaryRevisionDate), "day")) {
                return Promise.reject(
                  new Error("Next Salary Revision Date cannot be earlier than Previous Salary Revision Date")
                );
              }

              return Promise.resolve();
            },
          }),
        ];
      default:
        return undefined;
    }
  };

  const getJobDetailDependencies = (fieldKey: string) => {
    if (fieldKey === "contractStartDate") return ["employmentMode", "doj", "contractEndDate"];
    if (fieldKey === "contractEndDate") return ["employmentMode", "contractStartDate", "lwd"];
    if (fieldKey === "lwd") return ["doj", "contractEndDate", "staffStatusId"];
    if (fieldKey === "previousSalaryRevisionDate") return ["doj", "nextSalaryRevisionDate"];
    if (fieldKey === "nextSalaryRevisionDate") return ["previousSalaryRevisionDate"];
    return undefined;
  };

  const getJobDetailDisabledDate = (fieldKey: string) => (current: any) => {
    const dateOfJoining = form.getFieldValue("doj");
    const contractStartDate = form.getFieldValue("contractStartDate");
    const contractEndDate = form.getFieldValue("contractEndDate");
    const lastWorkingDay = form.getFieldValue("lwd");
    const previousSalaryRevisionDate = form.getFieldValue("previousSalaryRevisionDate");

    if (!current) return false;
    if (fieldKey === "contractStartDate") {
      return !!(
        (dateOfJoining && current < dayjs(dateOfJoining).startOf("day")) ||
        (contractEndDate && current > dayjs(contractEndDate).endOf("day"))
      );
    }
    if (fieldKey === "contractEndDate") {
      return !!(
        (contractStartDate && current < dayjs(contractStartDate).startOf("day")) ||
        (lastWorkingDay && current > dayjs(lastWorkingDay).endOf("day"))
      );
    }
    if ((fieldKey === "lwd" || fieldKey === "previousSalaryRevisionDate") && dateOfJoining) {
      return current < dayjs(dateOfJoining).startOf("day");
    }
    if (fieldKey === "nextSalaryRevisionDate" && previousSalaryRevisionDate) {
      return current < dayjs(previousSalaryRevisionDate).startOf("day");
    }
    return false;
  };

  const isJobDetailField = (fieldKey: string) =>
    [
      "staffName",
      "entity",
      "employeeId",
      "employmentMode",
      "employeeType",
      "designation",
      "role",
      "doj",
      "contractStartDate",
      "contractEndDate",
      "lwd",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "noticePeriod",
      "workLocation",
      "gradeBand",
      "previousSalaryRevisionDate",
      "nextSalaryRevisionDate",
    ].includes(fieldKey);

  useEffect(() => {
    const blockerCallback = (location: any) => {
      setPendingLocation(location);
     // onSaveEdit(); // This will trigger the summary modal
      return false; // Prevent navigation
    };

    setBlocker(hasUnsavedChanges ? blockerCallback : null);

    return () => setBlocker(null); // Cleanup on unmount
  }, [hasUnsavedChanges, setBlocker]);

  /** Email domain depends on the selected entity - same rule as the drawer. */
  const getRequiredEmailDomain = useCallback(
    (entityId?: string) => {
      const entityName =
        entityList.find((entity) => entity?.id === entityId)?.name ||
        (staff?.entity === entityId ? staff?.entityName : "") ||
        "";

      return String(entityName).trim().toLowerCase() === INNOTHRUST_ENTITY_NAME
        ? INNOTHRUST_EMAIL_DOMAIN
        : DEFAULT_STAFF_EMAIL_DOMAIN;
    },
    [entityList, staff]
  );

const CountryCodeInput: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [code, setCode] = useState("+91");
  const [number, setNumber] = useState("");

  useEffect(() => {
    if (value) {
      const parts = value.split(" ");
      const foundCode = COUNTRY_CODES.find((c) => c.value === parts[0]);
      if (foundCode && parts.length > 1) {
        setCode(foundCode.value);
        setNumber(parts.slice(1).join(" "));
      } else {
        // Fallback for numbers without a country code or with an unknown code
        setCode("+91"); // Default to IN
        setNumber(value);
      }
    } else {
      // Default state for new entries
      setCode("+91");
      setNumber("");
    }
  }, [value]);

  const triggerChange = (newCode: string, newNumber: string) => {
    if (onChange) {
      onChange(newNumber ? `${newCode} ${newNumber}`.trim() : "");
    }
  };

  const onCodeChange = (newCode: string) => {
    setCode(newCode);
    triggerChange(newCode, number);
  };

  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value;
    setNumber(newNumber);
    triggerChange(code, newNumber);
  };

  const countryCodeSelector = (
    <Select value={code} onChange={onCodeChange} style={{ minWidth: 80 }} disabled={disabled}>
      {COUNTRY_CODES.map((c) => (<Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>))}
    </Select>
  );

  return (
    <Input
      addonBefore={countryCodeSelector}
      value={number}
      onChange={onNumberChange}
      disabled={disabled}
    />
  );
};

  /** Contract fields show for every mode except Permanent; vendor fields only for Vendor Consultant. */
  const resolveEmploymentModeFlags = useCallback(
    (employmentModeId: any) => {
      const modeName = employmentModes.find(
        (mode) => mode?.id === employmentModeId
      )?.name;
      return {
        isVendor: modeName === VENDOR_CONSULTANT_MODE,
        isPermanent: modeName === PERMANENT_EMPLOYMENT_MODE,
      };
    },
    [employmentModes]
  );

  const renderEditableField = (config: {
    key: string;
    label: string;
    type: string;
    options?: any[];
    rules?: any[];
    disabled?: boolean;
    disabledDate?: (current: any) => boolean;
    dependencies?: string[];
  }) => {
    const {
      key: fieldKey,
      label,
      type,
      options,
      rules: configRules,
      disabled,
      disabledDate: configDisabledDate,
      dependencies: configDependencies,
    } = config;
    const isDropdown = type === "select";
    const isDate = type === "date";
    const isNumber = type === "number";
    const isTextarea = type === "textarea";
    const shouldApplyJobRules =
      (editingSection === "work" || (editingSection === "all" && activeTab === "job")) &&
      isJobDetailField(fieldKey);
    const fieldRules = shouldApplyJobRules ? getJobDetailRules(fieldKey) : configRules;
    const fieldDependencies = shouldApplyJobRules
      ? getJobDetailDependencies(fieldKey)
      : configDependencies;
    const fieldDisabledDate = shouldApplyJobRules
      ? getJobDetailDisabledDate(fieldKey)
      : configDisabledDate;
  const isPhone = type === "phone";
    return (
      <Form.Item
        name={fieldKey}
        label={label}
        key={fieldKey}
        rules={fieldRules}
        dependencies={fieldDependencies}
      >
        {isDropdown ? (
          <Select
            placeholder={`Select ${label}`}
            allowClear
            options={options}
            disabled={disabled}
          />
        ) : isDate ? (
          <DatePicker
           
            style={{ width: "100%" }}
           
            format="DD-MMM-YYYY"
            disabled={disabled}
            disabledDate={fieldDisabledDate}
          />
        ) : isNumber ? (
          <Input
            inputMode="numeric"
            onChange={(event) => {
              const onlyNums = event.target.value.replace(/\D/g, "");
              form.setFieldsValue({ [fieldKey]: onlyNums });
            }}
            onKeyDown={(event) => {
              if (
                !/[0-9]/.test(event.key) &&
                event.key !== "Backspace" &&
                event.key !== "Delete" &&
                event.key !== "ArrowLeft" &&
                event.key !== "ArrowRight" &&
                event.key !== "Tab"
              ) {
                event.preventDefault();
              }
            }}
          />
        ) : isTextarea ? (
          <Input.TextArea rows={4} disabled={disabled} />
        ) : isPhone ? (
          <CountryCodeInput disabled={disabled} />
        ) : (
          <Input
            disabled={disabled}
             onChange={(e) => {
              if (fieldKey === "aadhaar") {
                form.setFieldsValue({
                  aadhaar: formatAadhaar(e.target.value),
                });
              }

              if (fieldKey === "pan") {
                form.setFieldsValue({
                  pan: e.target.value.toUpperCase(),
                });
              }
            }}
            maxLength={
              fieldKey === "aadhaar" ? 14 :
              fieldKey === "uan" ? 12 :
              fieldKey === "pan" ? 10 : undefined
            }
          />
        )}
      </Form.Item>
    );
  };

  const fetchStaff = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const response = await Client.get(`${API_GET_STAFF_DETAILS}/${staffId}`);
      const data = response?.data?.data;
      const entity = Array.isArray(data) ? data[0] : data;
      setStaff({
        ...emptyStaff,
        ...(routedStaff || {}),
        ...(entity || {}),
        staffId: staffId,
      });      
    } catch {
      setStaff({ ...emptyStaff, ...(routedStaff || {}), staffId });
      form.setFieldsValue({});
      showNotification("Could not load latest staff details.", "warning");
    } finally {
      setLoading(false);
    }
  }, [routedStaff, staffId]);

  useEffect(() => {
    if (staff && staff.staffId && Object.keys(staff).length > 0) { // Ensure staff object is not empty
      // Merge original staff data with any temporary unsaved data for the current tab
      const mergedData = { ...staff, ...tempFormData };
      const formValues = {
        ...mergedData,
        presentAddress: mergedData.presentAddress || "",
        permanentAddress: mergedData.permanentAddress || "",
        dependents: normalizeDependents(parseDependents(mergedData.dependents)),
        positionHistory: toPositionHistoryFormValues(parsePositionHistory(mergedData.positionHistory)),
        doj: mergedData.doj ? dayjs(mergedData.doj) : null,
        contractStartDate: mergedData.contractStartDate
          ? dayjs(mergedData.contractStartDate)
          : null,
        contractEndDate: mergedData.contractEndDate
          ? dayjs(mergedData.contractEndDate)
          : null,
        lwd: mergedData.lwd ? dayjs(mergedData.lwd) : null,
        previousSalaryRevisionDate: mergedData.previousSalaryRevisionDate
          ? dayjs(mergedData.previousSalaryRevisionDate)
          : null,
        nextSalaryRevisionDate: mergedData.nextSalaryRevisionDate
          ? dayjs(mergedData.nextSalaryRevisionDate)
          : null,
         dateOfBirth: mergedData.dateOfBirth ? moment(mergedData.dateOfBirth) : null,
      };
      form.setFieldsValue(formValues); // Set form values after fetching and merging
    }
  }, [staff, tempFormData, form]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    return () => {
      if (tabLoadingTimerRef.current) {
        clearTimeout(tabLoadingTimerRef.current);
      }
    };
  }, []);

  const switchProfileTab = useCallback((key: StaffProfileTabKey) => {
    if (key === activeTab) return;

    if (tabLoadingTimerRef.current) {
      clearTimeout(tabLoadingTimerRef.current);
    }

    setTabLoading(true);
    setActiveTab(key);
    tabLoadingTimerRef.current = setTimeout(() => {
      setTabLoading(false);
      tabLoadingTimerRef.current = null;
    }, 180);
  }, [activeTab]);

  const getDocumentsFromResponse = useCallback((response: any) => {
    const data = response?.data?.data;
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchDocumentCounts = useCallback(async () => {
    if (!staffId || documentTypes.length === 0) {
      setDocumentCounts({});
      return;
    }

    try {
      const countEntries = await Promise.all(
        documentTypes.map(async (documentType) => {
          const response = await Client.get(`${API_GET_DOCUMENTS}${staffId}`, {
            params: { documentTypeId: documentType.id },
          });
          return [documentType.id, getDocumentsFromResponse(response).length] as const;
        })
      );
      const counts = countEntries.reduce((acc: Record<string, number>, [documentTypeId, count]) => {
        acc[documentTypeId] = count;
        return acc;
      }, {});

      setDocumentCounts(counts);
    } catch (error) {
      console.error("Failed to fetch staff document counts:", error);
      setDocumentCounts({});
    }
  }, [documentTypes, getDocumentsFromResponse, staffId]);

  const fetchStaffDocuments = useCallback(async (documentTypeId = activeDocumentCategoryId) => {
    if (!staffId || !documentTypeId) {
      setStaffDocuments([]);
      return;
    }
    setLoadingStaffDocuments(true);
    try {
      const response = await Client.get(`${API_GET_DOCUMENTS}${staffId}`, { params: { documentTypeId } });
      const documents = getDocumentsFromResponse(response);
      setStaffDocuments(documents);
      setDocumentCounts((currentCounts) => ({
        ...currentCounts,
        [documentTypeId]: documents.length,
      }));
    } catch (error) {
      console.error("Failed to fetch staff documents:", error);
      setStaffDocuments([]);
      showNotification("Failed to load staff documents.", "error");
    } finally {
      setLoadingStaffDocuments(false);
    }
  }, [activeDocumentCategoryId, getDocumentsFromResponse, staffId]);

  // Fetch Document Types for the left sidebar
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      setLoadingDocumentTypes(true);
      try {
        const response = await Client.get(API_GET_ALL_DOCUMENT_TYPE_ACTIVELIST);
        const fetchedTypes: DocumentType[] = response?.data?.data || [];
        setDocumentTypes(fetchedTypes);
        if (fetchedTypes.length > 0) {
          setActiveDocumentCategoryId(fetchedTypes[0].id); // Set the first document type as active
        }
      } catch (error) {
        console.error("Failed to fetch document types:", error);
        showNotification("Failed to load document types.", "error");
      } finally {
        setLoadingDocumentTypes(false);
      }
    };
    fetchDocumentTypes();
  }, []); // Run once on mount

  // Fetch Staff Documents based on selected Document Type
  useEffect(() => {
    fetchStaffDocuments();
  }, [fetchStaffDocuments]); // Re-fetch when staffId or activeDocumentCategoryId changes

  useEffect(() => {
    fetchDocumentCounts();
  }, [fetchDocumentCounts]);

  const handleDocumentUploadSuccess = useCallback((documentTypeId: string) => {
    setActiveDocumentCategoryId(documentTypeId);
    fetchStaffDocuments(documentTypeId);
    fetchDocumentCounts();
  }, [fetchDocumentCounts, fetchStaffDocuments]);

  const handleDownloadDocument = useCallback(async (document: any) => {
    if (!document?.id) {
      showNotification("Document id is not available.", "error");
      return;
    }

    setDocumentActionLoading(document.id);
    try {
      const response = await Client.get(`${API_GET_DOCUMENT_SIGNED_URL}${document.id}`);
      const downloadUrl = response?.data?.data?.url || response?.data?.url;

      if (!downloadUrl) {
        showNotification("Download link is not available for this document.", "error");
        return;
      }

      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = response?.data?.data?.fileName || document?.fileName || document?.originalFileName || "document";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download document:", error);
      showNotification("Failed to download document.", "error");
    } finally {
      setDocumentActionLoading(null);
    }
  }, []);

  const handleDeleteDocument = useCallback((document: any) => {
    if (!document?.id) {
      showNotification("Document id is not available.", "error");
      return;
    }

    Modal.confirm({
      title: "Delete Document",
      content: `Are you sure you want to delete ${document?.fileName || "this document"}?`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        setDocumentActionLoading(document.id);
        try {
          await Client.delete(`${API_DELETE_DOCUMENT}${document.id}`);
          showNotification("Document deleted successfully.", "success");
          await fetchStaffDocuments(document.documentTypeId || activeDocumentCategoryId);
          await fetchDocumentCounts();
        } catch (error) {
          console.error("Failed to delete document:", error);
          showNotification("Failed to delete document.", "error");
        } finally {
          setDocumentActionLoading(null);
        }
      },
    });
  }, [activeDocumentCategoryId, fetchDocumentCounts, fetchStaffDocuments]);

  const fetchActivityLogs = useCallback(async () => {
    if (!staffId) return;
    try {
      const response = await Client.get(API_GET_ACTIVITY_LOG + `${staffId}`);
      setActivityLogData(response?.data?.data || []);
    } catch {
      setActivityLogData([]);
    }
  }, [staffId]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  useEffect(() => {
    const fetchMasterList = async (module: string, setter: (items: any[]) => void) => {
      try {
        const response = await Client.get(API_COMMON_GET_BY_MODULE(module));
        setter(response?.data?.data?.items || response?.data?.data || []);
      } catch {
        setter([]);
      }
    };

    fetchMasterList("master_employee_type", setEmployeeTypes);
    fetchMasterList("master_employment_mode", setEmploymentModes);
    fetchMasterList("master_gender", setGenders); // Assuming these are for dropdowns
    fetchMasterList("master_marital_status", setMaritalStatuses);
    fetchMasterList("master_blood_group", setBloodGroups);
    fetchStaffList();
    fetchProjectList();    
    fetchVendorList();
    fetchDepartmentList();
    fetchEntityList();
  }, []);

  const fetchStaffList = async () => {
      const response = await Client.get(API_GET_ALL_STAFF_LIST);
      if (response?.data?.data) {
        setLineManagers(response?.data?.data);
      } else {
        setLineManagers([]);
      }
    };
  
    const fetchProjectList = async () => {
      const response = await Client.get(API_GET_ALL_PROJECT_LIST);
      if (response?.data?.data) {
        setProjects(response?.data?.data);
      } else {
        setProjects([]);
      }
    };
  
   
    const fetchDepartmentList = async () => {
      const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
      if (response?.data?.data) {
        setDepartments(response?.data?.data);
      } else {
        setDepartments([]);
      }
    };

  const fetchEntityList = useCallback(async () => {
    try {
      const response = await Client.get(API_COMMON_GET_BY_MODULE("master_entity"));
      setEntityList(response?.data?.data?.items || []);
    } catch {
      setEntityList([]);
    }
  }, []);

  const fetchVendorList = useCallback(async () => {
    try {
      const response = await Client.get(API_GET_ALL_VENDOR_LIST);
      setVendorList(response?.data?.data || []);
    } catch {
      setVendorList([]);
    }
  }, []);

  const fetchSubDepartmentsList = useCallback(async (departmentId: string) => {
    if (!departmentId) {
      setSubDepartmentList([]);
      return;
    }
    try {
      const response = await Client.get(`${API_GET_SUBDEPARTMENTS_BY_DEPARTMENT}${departmentId}`);
      setSubDepartmentList(response?.data?.data || []);
    } catch {
      setSubDepartmentList([]);
    }
  }, []);

  const fetchAccountListByEntity = useCallback(async (entityId: string) => {
    if (!entityId) {
      setAccountList([]);
      return;
    }
    try {
      const response = await Client.get(`${API_GET_ACCOUNT_LIST_BY_ENTITY}${entityId}`);
      setAccountList(response?.data?.data || []);
    } catch {
      setAccountList([]);
    }
  }, []);

  const mock = defaultStaffProfileMock;
  const activityEntries = buildActivityEntries(activityLogData, staff);
  const recentActivities =
    activityEntries.length > 0
      ? activityEntries.slice(0, 4).map((entry) => ({
          title: entry.title,
          date: entry.date,
        }))
      :  mock.overview.recentActivity;
  const timelineItems =
    activityEntries.length > 0
      ? [...activityEntries].reverse().map((entry) => ({
          date: entry.date,
          title: entry.title,
          description: entry.description,
        }))
      : mock.timeline;
  const status = valueOrFallback(staff?.staffStatusId);
  const getStaffStatusColor = (statusValue: string) => {
    if (statusValue === "ACTIVE") return "success";
    if (statusValue === "DRAFT") return "processing";
    if (statusValue === "NOTICE PERIOD") return "warning";
    if (statusValue === "INACTIVE") return "red";
    return "default";
  };
  const isExited = status === "INACTIVE";
  const isExitPreview = activeTab === "exit";
  const staffName = valueOrFallback(staff?.staffName, "Staff Member");
  const employeeId = valueOrFallback(staff?.employeeId, "-");
  const entityName = valueOrFallback(
    firstRealValue(
      staff?.entityName,
      resolveMasterName(staff?.entity, entityList)
    ),
    "-"
  );
  const department = valueOrFallback(staff?.departmentName, "-");
  const subDepartment = valueOrFallback(staff?.subDepartmentName, "-");
  const lineManager = valueOrFallback(firstRealValue(staff?.lineManager, staff?.lineManagerName), "-");
  const designation = valueOrFallback(staff?.designation, "-");
  const role = valueOrFallback(staff?.role, "-");
  const employmentMode = valueOrFallback(
    firstRealValue(
      staff?.employmentModeName,
      resolveMasterName(staff?.employmentMode, employmentModes)
    ),
    "-"
  );
  const employeeType = valueOrFallback(
    firstRealValue(
      staff?.employeeTypeName,
      resolveMasterName(staff?.employeeType, employeeTypes)
    ),
    "-"
  ); 
  const joiningDate = dateOrFallback(staff?.doj, "-");
  const contractStartDate = dateOrFallback(staff?.contractStartDate, "-");
  const contractEndDate = dateOrFallback(staff?.contractEndDate, "-");
  const lastWorkingDay = dateOrFallback(staff?.lwd, "-");
  const noticePeriod = formatDays(staff?.noticePeriod);
  // Work location is not captured for most staff yet - default to Remote.
  const workLocation = valueOrFallback(staff?.workLocation, "-");
  const email = valueOrFallback(staff?.email, "-");
  const client = valueOrFallback(firstRealValue(staff?.accountName, staff?.currentMonthTopAccountName), "-");
  const project = valueOrFallback(firstRealValue(staff?.projectName, staff?.currentMonthTopProjectName), "-");
  const currentCtc = formatMoney(staff?.ctc, staff?.salaryCurrency);
  const gradeBand = valueOrFallback(staff?.gradeBand, "-");
   const previousSalaryRevisionDate = dateOrFallback(staff?.previousSalaryRevisionDate, "-");
  const nextSalaryRevisionDate = dateOrFallback(staff?.nextSalaryRevisionDate, "-");
  const nationality = valueOrFallback(staff?.nationality, "-");
  const pan = valueOrFallback(staff?.pan, "-");
  const lastAppraisal = valueOrFallback(staff?.lastAppraisal, "-");
  const leaveBalance = valueOrFallback(staff?.leaveBalance, "-");
  const aadhaar = formatAadhaar(staff?.aadhaar);  
  const tenure = calculateTenure(staff?.doj, staff?.lwd);
  const previousRevisionDate = dateOrFallback(staff?.previousSalaryRevisionDate, "-");

  const metrics: StaffProfileMetric[] = [
    { label: "Tenure", value: tenure, note: joiningDate !== "-" ? `since ${joiningDate}` : "-" },
    { label: "Current CTC", value: currentCtc, note: previousRevisionDate !== "-" ? `revised ${previousRevisionDate}` : "-" },
    // No appraisal table exists yet - default to an A+ grade for the current
    // financial year until appraisal data is stored.
    { label: "Last Appraisal", value: lastAppraisal, note: `${getCurrentFinancialYear()} cycle` },
    // Leave data does not exist in the system yet - show zero instead of the
    // mock value until the leave module is built.
    { label: "Leave Balance", value: leaveBalance, note: "across all types" },
  ];

  const MARITAL_STATUS_OPTIONS = [
  { label: "Single", value: "SINGLE" },
  { label: "Married", value: "MARRIED" },
  { label: "Divorced", value: "DIVORCED" },
  { label: "Widowed", value: "WIDOWED" },
];

const BLOOD_GROUP_OPTIONS = [
  { label: "A+", value: "A+" },
  { label: "A-", value: "A-" },
  { label: "B+", value: "B+" },
  { label: "B-", value: "B-" },
  { label: "AB+", value: "AB+" },
  { label: "AB-", value: "AB-" },
  { label: "O+", value: "O+" },
  { label: "O-", value: "O-" },
];

  const employmentSnapshot = withRealValues(mock.overview.employmentSnapshot, {
    "Employment Mode": employmentMode,
    "Employee Type": employeeType,
    "Sub Department": subDepartment,
    "Work Location": workLocation, // Assuming this is a text field
    "Notice Period": noticePeriod,
    "Grade / Band": valueOrFallback(staff?.gradeBand, "-"),
    "Previous Salary Revision Date": previousSalaryRevisionDate,
    "Next Salary Revision Date": nextSalaryRevisionDate,
  });

  const workDetails = withRealValues(mock.job.workDetails, {
    Entity: entityName,
    "Employee Id": employeeId,
    "Employment Mode": employmentMode,
    "Employee Type": employeeType,
    Designation: designation,
    Role: role,
    "Date of Joining": joiningDate,
    "Last Working Day": lastWorkingDay,
    Department: department,
    "Sub Department": subDepartment,
    "Line Manager": lineManager,
    "Notice Period": noticePeriod,
    "Work Location": workLocation,
    "Grade / Band": gradeBand,
    "Previous Salary Revision Date": previousSalaryRevisionDate,
    "Next Salary Revision Date": nextSalaryRevisionDate,
    "Contract Start Date": contractStartDate,
    "Contract End Date": contractEndDate,
  });
  const shouldShowStaffContractFields =
    employmentMode !== PERMANENT_EMPLOYMENT_MODE &&
    !resolveEmploymentModeFlags(staff?.employmentMode).isPermanent;
  const visibleWorkDetails = shouldShowStaffContractFields
    ? workDetails
    : workDetails.filter(
        (field) =>
          !["Contract Start Date", "Contract End Date"].includes(field.label)
      );

  const basicInformation = withRealValues(mock.personal.basicInformation, {
    "Staff Name": staffName,
    "Date of Birth": dateOrFallback(staff?.dateOfBirth || staff?.dob, "-"),
    Gender: valueOrFallback(
      firstRealValue(staff?.genderName, resolveMasterName(staff?.gender, genders)),
      "-"
    ),
    Nationality: nationality,
    PAN: pan,
    Aadhaar: formatAadhaar(aadhaar),
    "Blood Group": valueOrFallback(resolveMasterName(staff?.bloodGroup, bloodGroups), "-"),
    "Marital Status": valueOrFallback(resolveMasterName(staff?.maritalStatus, maritalStatuses), "-"),
    "UAN (PF)": valueOrFallback(staff?.uan),
  });
  
  const contactDetails = withRealValues(mock.personal.contactDetails, {
    "Work Email": email,
    "Personal Email": valueOrFallback(staff?.personalEmail, "-"),
    Mobile: valueOrFallback(firstRealValue(staff?.mobile, staff?.phoneNumber), "-"),
    "Emergency Contact": valueOrFallback(staff?.emergencyContact, "-"),
  });
  const presentAddress = valueOrFallback(staff?.presentAddress, "");
  const permanentAddress = valueOrFallback(staff?.permanentAddress, "");
  const dependents = parseDependents(staff?.dependents);
  const displayDependents = dependents;
  const positionHistory = parsePositionHistory(staff?.positionHistory);
  const displayPositionHistory = positionHistory;

  
  const maritalStatusOptions = maritalStatuses.length > 0
    ? maritalStatuses.map((ms) => ({ label: ms.name, value: ms.id }))
    : [
        { label: "Single", value: "Single" },
        { label: "Married", value: "Married" },
        { label: "Divorced", value: "Divorced" },
        { label: "Widowed", value: "Widowed" },
      ];
  const getOptionLabel = (
  value: any,
  options: any[],
  fallbackValue: any
) => {
  const option = options?.find(
    (opt) =>
      opt.value === value ||
      opt.id === value ||
      opt.departmentId === value ||
      opt.staffId === value ||
      opt.accountId === value ||
      opt.projectId === value
  );

  return option
    ? option.label ||
        option.name ||
        option.departmentName ||
        option.staffName ||
        option.accountName ||
        option.projectName ||
        option.subDepartmentName ||
        option.entityName ||
        option.vendorName 
    : fallbackValue;
};
  const editableFieldsConfig = useMemo(
    () => [
      // Entity
      {
        key: "staffName",
        label: "Staff Name",
        type: "text",
        getValue: (s: any) => s?.staffName,
        oldValue: staffName,
        newValue: (val: any) => val,
        rules: [{ required: true, message: "Please enter Staff Name" },
          { pattern: /^[A-Za-z\s.'/-]+$/, message: "Please enter a valid Staff Name. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed." }],
      },
      {
        key: "entity",
        label: "Entity",
        type: "select",
        options: entityList.map((e) => ({ label: e.name, value: e.id })),
        getValue: (s: any) => s?.entity,
        oldValue: entityName,
        newValue: (val: any) => getOptionLabel(val, entityList, entityName),
        rules: [{ required: true, message: "Please select Entity" }],
      },
      {
        key: "employeeId",
        label: "Employee Id",
        type: "text",
        getValue: (s: any) => s?.employeeId,
        oldValue: employeeId,
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Employee Id" },
          {
            validator: (_: any, value: string) => {
              if (!value) return Promise.resolve();
              const regex = /^[A-Za-z]{2,3}[0-9]{5}$/;
              return regex.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Invalid format. Please check the Info."));
            },
          },
        ],
      },
      // Job Details / Work Details
      {
        key: "employmentMode",
        label: "Employment Mode",
        type: "select",
        options: employmentModes.map((em) => ({ label: em.name, value: em.id })),
        getValue: (s: any) => s?.employmentMode, // Raw value from staff
        oldValue: getOptionLabel(staff?.employmentMode, employmentModes, employmentMode),
        newValue: (val: any) => getOptionLabel(val, employmentModes, employmentMode),
      },
      {
        key: "employeeType",
        label: "Employee Type",
        type: "select",
        options: employeeTypes.map((et) => ({ label: et.name, value: et.id })),
        getValue: (s: any) => s?.employeeType, // Raw value from staff
        oldValue: getOptionLabel(staff?.employeeType, employeeTypes, employeeType),
        newValue: (val: any) => getOptionLabel(val, employeeTypes, employeeType),
      },
      // Staff Status
      {
        key: "staffStatusId",
        label: "Staff Status",
        type: "select",
        options: [
          { value: "DRAFT", label: "DRAFT" }, { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" }, { value: "NOTICE PERIOD", label: "NOTICE PERIOD" },
        ],
        getValue: (s: any) => s?.staffStatusId, oldValue: valueOrFallback(staff?.staffStatusId), newValue: (val: any) => valueOrFallback(val),
      },
      { key: "designation", label: "Designation", type: "text", getValue: (s: any) => s?.designation, oldValue: designation, newValue: (val: any) => val },
      { key: "role", label: "Role", type: "text", getValue: (s: any) => s?.role, oldValue: role, newValue: (val: any) => val },
      { key: "doj", label: "Date of Joining", type: "date", getValue: (s: any) => s?.doj, oldValue: joiningDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "contractStartDate", label: "Contract Start Date", type: "date", disabled: true, getValue: (s: any) => s?.contractStartDate, oldValue: contractStartDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "contractEndDate", label: "Contract End Date", type: "date", getValue: (s: any) => s?.contractEndDate, oldValue: contractEndDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      { key: "lwd", label: "Last Working Day", type: "date", getValue: (s: any) => s?.lwd, oldValue: lastWorkingDay, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      {
        key: "departmentId",
        label: "Department",
        type: "select",
        options: departments.map((d) => ({ label: d.departmentName, value: d.departmentId })),
        getValue: (s: any) => s?.departmentId, // Raw value from staff
        oldValue: getOptionLabel(staff?.departmentId, departments, department),
        newValue: (val: any) => getOptionLabel(val, departments, department),
      },
      {
        key: "subDepartmentId",
        label: "Sub Department",
        type: "select",
        options: subDepartmentList.map((sd) => ({ label: sd.subDepartmentName, value: sd.id })),
        getValue: (s: any) => s?.subDepartmentId,
        oldValue: getOptionLabel(staff?.subDepartmentId, subDepartmentList, subDepartment),
        newValue: (val: any) => getOptionLabel(val, subDepartmentList, subDepartment),
      },
      {
        key: "lineManagerId",
        label: "Line Manager",
        type: "select",
        options: lineManagers.map((lm) => ({ label: lm.staffName, value: lm.staffId })),
        getValue: (s: any) => s?.lineManagerId, // Raw value from staff
        oldValue: getOptionLabel(staff?.lineManagerId, lineManagers, lineManager),
        newValue: (val: any) => getOptionLabel(val, lineManagers, lineManager),
      },
      { key: "noticePeriod", label: "Notice Period", type: "number", getValue: (s: any) => s?.noticePeriod, oldValue: staff?.noticePeriod, newValue: (val: any) => val },
      { key: "workLocation", label: "Work Location", type: "text", getValue: (s: any) => s?.workLocation, oldValue: workLocation, newValue: (val: any) => val },
      { key: "gradeBand", label: "Grade / Band", type: "text", getValue: (s: any) => s?.gradeBand, oldValue: gradeBand, newValue: (val: any) => val },
    { key: "previousSalaryRevisionDate", label: "Previous Salary Revision Date", type: "date", getValue: (s: any) => s?.previousSalaryRevisionDate, oldValue: previousSalaryRevisionDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
    { key: "nextSalaryRevisionDate", label: "Next Salary Revision Date", type: "date", getValue: (s: any) => s?.nextSalaryRevisionDate, oldValue: nextSalaryRevisionDate, newValue: (val: any) => val ? dayjs(val).format("YYYY-MM-DD") : "-" },
      {
        key: "accountId",
        label: "Client",
        type: "select",
        options: accountList.map((c) => ({ label: c.accountName, value: c.accountId })),
        getValue: (s: any) => s?.accountId, // Raw value from staff
        oldValue: getOptionLabel(staff?.accountId, accountList, client),
        newValue: (val: any) => getOptionLabel(val, accountList, client),
      },
      {
        key: "projectId",
        label: "Project",
        type: "select",
        options: filteredProjectList.map((p) => ({ label: p.projectName, value: p.projectId })),
        getValue: (s: any) => s?.projectId, // Raw value from staff
        oldValue: getOptionLabel(staff?.projectId, filteredProjectList, project),
        newValue: (val: any) => getOptionLabel(val, filteredProjectList, project),
      },
      {
        key: "vendorId",
        label: "Vendor",
        type: "select",
        options: vendorList.map((v) => ({ label: v.vendorName, value: v.vendorId })),
        getValue: (s: any) => s?.vendorId,
        oldValue: getOptionLabel(staff?.vendorId, vendorList, valueOrFallback(staff?.vendorName)),
        newValue: (val: any) => getOptionLabel(val, vendorList, valueOrFallback(staff?.vendorName)),
      },
      // Personal Details / Basic Information
      { key: "dateOfBirth", label: "Date of Birth", type: "date", getValue: (s: any) => s?.dateOfBirth || s?.dob, oldValue: dateOrFallback(staff?.dateOfBirth || staff?.dob), newValue: (val: any) => val ? moment(val).format("DD-MMM-YYYY") : "-" },
      {
        key: "gender",
        label: "Gender",
        type: "select",
        options: genders.map((g) => ({ label: g.name, value: g.id })),
        rules: [{ required: true, message: "Please select Gender" }],
        getValue: (s: any) => s?.gender, // Raw value from staff
        oldValue: getOptionLabel(staff?.gender, genders, valueOrFallback(staff?.genderName)),
        newValue: (val: any) => getOptionLabel(val, genders, valueOrFallback(staff?.genderName)),
      },
      {
        key: "nationality",
        label: "Nationality",
        type: "select",
        options: NATIONALITY_OPTIONS,
        getValue: (s: any) => s?.nationality,
        oldValue: getOptionLabel(staff?.nationality, NATIONALITY_OPTIONS, nationality),
        newValue: (val: any) => getOptionLabel(val, NATIONALITY_OPTIONS, val),
      },
      {
        key: "pan",
        label: "PAN",
        type: "text",
        getValue: (s: any) => s?.pan,
        oldValue: pan?.toUpperCase(),
        newValue: (val: any) => val?.toUpperCase(),
        rules: [{ pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: "Please enter a valid PAN number." }],
      },
      {
        key: "aadhaar",
        label: "Aadhaar",
        type: "text",
        getValue: (s: any) => formatAadhaar(s?.aadhaar),
        oldValue: formatAadhaar(aadhaar),
        newValue: (val: any) => formatAadhaar(val),
        rules: [
          {
            validator: async (_: any, value: string) => {
              if (!value) {
                return Promise.resolve(); // Optional field
              }
              if (/^\d{4}\s\d{4}\s\d{4}$/.test(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Please enter a valid 12-digit Aadhaar number."));
            },
          },
        ],
      },
      {
        key: "bloodGroup",
        label: "Blood Group",
        type: "select",
        options: BLOOD_GROUP_OPTIONS.map((bg) => ({
          label: bg.label,
          value: bg.value,
        })),
        getValue: (s: any) => s?.bloodGroup,
        oldValue: getOptionLabel(
          staff?.bloodGroup,
          BLOOD_GROUP_OPTIONS,
          valueOrFallback(staff?.bloodGroupName)
        ),
        newValue: (val: any) =>
          getOptionLabel(
            val,
            BLOOD_GROUP_OPTIONS,
            valueOrFallback(staff?.bloodGroupName)
          ),
      },
      {
        key: "maritalStatus",
        label: "Marital Status",
        type: "select",
        options: MARITAL_STATUS_OPTIONS.map((ms) => ({
          label: ms.label,
          value: ms.value,
        })),
        getValue: (s: any) => s?.maritalStatus,
        oldValue: getOptionLabel(
          staff?.maritalStatus,
          MARITAL_STATUS_OPTIONS,
          valueOrFallback(staff?.maritalStatusName),
        ),
        newValue: (val: any) =>
          getOptionLabel(
            val,
            MARITAL_STATUS_OPTIONS,
            valueOrFallback(staff?.maritalStatusName)
          ),
      },
      {
        key: "contractStartDate",
        label: "Contract Start Date",
        type: "date",
        getValue: (s: any) => s?.contractStartDate,
        oldValue: dateOrFallback(staff?.contractStartDate),
        newValue: (val: any) => (val ? moment(val).format("YYYY-MM-DD") : "-"),
      },
      {
        key: "contractEndDate",
        label: "Contract End Date",
        type: "date",
        getValue: (s: any) => s?.contractEndDate,
        oldValue: dateOrFallback(staff?.contractEndDate),
        newValue: (val: any) => (val ? moment(val).format("YYYY-MM-DD") : "-"),
      },
       {
        key: "uan",
        label: "UAN (PF)",
        type: "text",
        getValue: (s: any) => s?.uan,
        oldValue: valueOrFallback(staff?.uan),
        newValue: (val: any) => val,
        rules: [
          { pattern: /^\d{12}$/, message: "Please enter a valid 12-digit UAN number." }
        ],
      },
      // Personal Details / Contact Details
      {
        key: "email",
        label: "Work Email",
        type: "text",
        getValue: (s: any) => s?.email,
        oldValue: email,
        newValue: (val: any) => val,
        rules: [
          { required: false, message: "Please enter Email" },
          { type: "email", message: "Please enter a valid Email" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve();

              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(value)) return Promise.resolve();

              const requiredDomain = getRequiredEmailDomain(getFieldValue("entity"));

              if (value.toLowerCase().endsWith(requiredDomain)) return Promise.resolve();

              return Promise.reject(new Error(`Email must end with ${requiredDomain}`));
            },
          }),
        ],
      },
     
      {
        key: "personalEmail",
        label: "Personal Email",
        type: "text",
        getValue: (s: any) => s?.personalEmail,
        oldValue: valueOrFallback(staff?.personalEmail),
        newValue: (val: any) => val,
        rules: [{ type: "email", message: "Please enter a valid Personal Email" }],
      },
      {
        key: "mobile",
        label: "Mobile",
        type: "phone",
        getValue: (s: any) => s?.mobile || s?.phoneNumber,
        oldValue: valueOrFallback(staff?.mobile || staff?.phoneNumber),
        newValue: (val: any) => val,
        rules: [
          { required: true, message: "Please enter Mobile Number" },
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve();
              const [code, number] = (value || "").split(" ");
              const country = COUNTRY_CODES.find((c) => c.value === code);
              if (country && country.pattern.test(number)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Please enter a valid mobile number."));
            },
          }),
        ],
      },
      {
        key: "emergencyContact",
        label: "Emergency Contact",
        type: "phone",
        getValue: (s: any) => s?.emergencyContact,
        oldValue: valueOrFallback(staff?.emergencyContact),
        newValue: (val: any) => val,
        rules: [
          ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
              if (!value) return Promise.resolve(); // Optional field
              const [code, number] = (value || "").split(" ");
              const country = COUNTRY_CODES.find((c) => c.value === code);
              if (country && country.pattern.test(number)) return Promise.resolve();
              return Promise.reject(new Error("Please enter a valid mobile number."));
            },
          }),
        ],
      },
      {
        key: "presentAddress",
        label: "Present Address",
        type: "textarea",
        getValue: (s: any) => s?.presentAddress,
        oldValue: presentAddress,
        newValue: (val: any) => valueOrFallback(val),
        rules: [
          {
            pattern: /^[A-Za-z0-9\s.,'/#-]*$/,
            message:
              "Please enter a valid address. Only letters, numbers, spaces, and special characters (.,'/#-) are allowed.",
          },
        ],
      },
      { key: "permanentAddress", label: "Permanent Address", type: "textarea", getValue: (s: any) => s?.permanentAddress, oldValue: permanentAddress, newValue: (val: any) => valueOrFallback(val) },
      {
        key: "dependents",
        label: "Dependents",
        type: "json",
        getValue: (s: any) => parseDependents(s?.dependents),
        oldValue: formatDependentsSummary(dependents),
        newValue: (val: any) => formatDependentsSummary(val),
      },
      {
        key: "positionHistory",
        label: "Position History",
        type: "positionHistory",
        getValue: (s: any) => parsePositionHistory(s?.positionHistory),
        oldValue: formatPositionHistorySummary(positionHistory),
        newValue: (val: any) => formatPositionHistorySummary(val),
      },
    ],
    [
      staff,
      staffName,
      departments,
      employeeTypes,
      employmentModes,
      lineManagers,
      clients,
      projects,
      genders,
      entityList,
      vendorList,
      subDepartmentList,
      accountList,
      maritalStatuses,
      bloodGroups,
      department,
      employeeType,
      subDepartment,
      workLocation,
      gradeBand,
      noticePeriod,
      lineManager,
      previousSalaryRevisionDate,
      nextSalaryRevisionDate,
      client,
      project,
      joiningDate,
      contractStartDate,
      contractEndDate,
      lastWorkingDay,
      nationality,
      pan,
      aadhaar,
      email,
      presentAddress,
      permanentAddress,
      dependents,
      positionHistory,
      filteredProjectList,
      fetchAccountListByEntity, fetchSubDepartmentsList // Added as dependencies for dynamic options
    ]
  );

  const onEditStaffDetails = () => {
    setIsEditing(true);
    // Set form fields with current staff data, converting values for DatePicker
    const formValues: any = { ...staff };
    if (formValues.doj) formValues.doj = dayjs(formValues.doj);
    if (formValues.dateOfBirth) formValues.dateOfBirth = dayjs(formValues.dateOfBirth);
    form.setFieldsValue(formValues);
  };

  const onCancelEdit = () => {
    setEditingSection(null);
    setTempFormData({});
    setHasUnsavedChanges(false);
    form.resetFields(); // Reset form to initial values
  };

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardChangesModal(true);
    } else {
      onCancelEdit();
      setEditingSection(null);
    }
  };




  const handleTabChange = (key: StaffProfileTabKey) => {
    if (hasUnsavedChanges) {
      setPendingTab(key);
      setShowUnsavedChangesModal(true);
    } else {
      switchProfileTab(key);
    }
  };

  const handleSaveAndContinue = () => {
    const currentValues = getCurrentVisibleFormValues();
    setTempFormData((prev: any) => ({ ...prev, ...currentValues }));
    setHasUnsavedChanges(false); // Changes are now "saved" in temp state
    setShowUnsavedChangesModal(false);
    if (pendingTab) {
      switchProfileTab(pendingTab);
      setPendingTab(null);
    }
  };
  const handleFormValuesChange = useCallback((changedValues: any, allValues: any) => {
    // Dynamic dropdown logic
    if ("entity" in changedValues) {
      fetchAccountListByEntity(changedValues.entity);
      form.setFieldsValue({ accountId: undefined, projectId: undefined }); // Clear dependent fields
      setFilteredProjectList([]); // Clear filtered projects
    }
    if ("accountId" in changedValues) {
      // Filter projects based on selected client
      const selectedClient = changedValues.accountId;
      if (selectedClient) {
        const projectsForClient = projects.filter(p => p.accountId === selectedClient);
        setFilteredProjectList(projectsForClient);
      } else {
        setFilteredProjectList([]);
      }
      form.setFieldsValue({ projectId: undefined }); // Clear project
    }
    if ("departmentId" in changedValues) {
      fetchSubDepartmentsList(changedValues.departmentId);
      form.setFieldsValue({ subDepartmentId: undefined }); // Clear sub-department
    }

    if ("doj" in changedValues) {
      const permanentEmploymentMode = employmentModes.find((type: any) => type.name === "Permanent");
      if (allValues.employmentMode && allValues.employmentMode !== permanentEmploymentMode?.id) {
        form.setFieldsValue({ contractStartDate: allValues.doj || null });
      }
    }

    // If employmentMode changes, clear dependent fields based on the selected mode.
    if ("employmentMode" in changedValues) {
      const selectedEmploymentMode = employmentModes.find(em => em.id === changedValues.employmentMode);
      if (selectedEmploymentMode && selectedEmploymentMode.name !== 'Vendor Consultant') {
        form.setFieldsValue({ vendorId: undefined });
      }
      const permanentEmploymentMode = employmentModes.find((type: any) => type.name === "Permanent");
      const isPermanent = changedValues.employmentMode === permanentEmploymentMode?.id;
      setShowContractFields(!isPermanent);
      form.setFieldsValue({
        contractStartDate: isPermanent ? null : allValues.doj || null,
        contractEndDate: null,
      });
    }

    let changesDetected = false;
    for (const config of getVisibleEditableFieldsConfig()) {
      const originalRawValue = config.getValue(staff);
      let currentFormValue = allValues[config.key];

      const normalizedOriginal = normalizeComparableValue(originalRawValue, config.type);
      const normalizedCurrent = normalizeComparableValue(currentFormValue, config.type);

      if (normalizedOriginal !== normalizedCurrent) {
        changesDetected = true;
        break;
      }
    }
    setHasUnsavedChanges(changesDetected);

    // Preserve existing onValuesChange logic if any, or integrate it here.
    // For now, assuming no other side effects are needed directly from form value changes
    // other than unsaved changes detection and dynamic dropdown updates.
  }, [staff, editableFieldsConfig]);

  // Override the global onValuesChange for the Form component
  const onFormValuesChange = (changedValues: any, allValues: any) => {
    handleFormValuesChange(changedValues, allValues);
    // Any other global onValuesChange logic can go here if needed
  };

  const getVisibleEditableFieldKeys = () => {
    const workFields = [
      "entity",
      "employeeId",
      "employmentMode",
      "employeeType",
      "designation",
      "role",
      "doj",
      ...(showContractFields ? ["contractStartDate", "contractEndDate"] : []),
      "previousSalaryRevisionDate",
      "nextSalaryRevisionDate",
      "lwd",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "noticePeriod",
      "workLocation",
      "gradeBand",
    ];
    const basicFields = [
      "staffName",
      "dateOfBirth",
      "gender",
      "nationality",
      "pan",
      "aadhaar",
      "bloodGroup",
      "maritalStatus",
      "uan",
    ];
    const contactFields = ["email", "personalEmail", "mobile", "emergencyContact"];

    if (editingSection === "work") return workFields;
    if (editingSection === "basic") return basicFields;
    if (editingSection === "contact") return contactFields;
    if (editingSection === "presentAddress") return ["presentAddress"];
    if (editingSection === "permanentAddress") return ["permanentAddress"];
    if (editingSection === "dependents") return ["dependents"];
    if (editingSection === "positionHistory") return ["positionHistory"];

    if (editingSection === "all") {
      if (activeTab === "job") return [...workFields, "positionHistory"];
      if (activeTab === "personal") {
        return [
          ...basicFields,
          ...contactFields,
          "presentAddress",
          "permanentAddress",
          "dependents",
        ];
      }
    }

    return editableFieldsConfig.map((config) => config.key);
  };

  const getVisibleEditableFieldKeySet = () =>
    new Set(getVisibleEditableFieldKeys());

  const getVisibleEditableFieldsConfig = () => {
    const visibleKeys = getVisibleEditableFieldKeySet();
    const usedKeys = new Set<string>();

    return editableFieldsConfig.filter((config) => {
      if (!visibleKeys.has(config.key) || usedKeys.has(config.key)) {
        return false;
      }

      usedKeys.add(config.key);
      return true;
    });
  };

  const getCurrentVisibleFormValues = () =>
    form.getFieldsValue(getVisibleEditableFieldKeys());

  const onSaveEdit = () => {
    form.validateFields(getVisibleEditableFieldKeys()).then(async () => {
      // Combine original staff data with all temporary changes from all tabs
      const allTempData = tempFormData;
      const finalFormState = { ...staff, ...allTempData, ...getCurrentVisibleFormValues() };

      const changedFields: any[] = [];

      for (const config of getVisibleEditableFieldsConfig()) {
        const originalValue = config.getValue(staff);
        let currentValue = finalFormState[config.key];

        if (config.type === "date") {
          currentValue = normalizeDatePayloadValue(currentValue);
        }

        const normalizedOriginal = normalizeComparableValue(originalValue, config.type);
        const normalizedCurrent = normalizeComparableValue(currentValue, config.type);

        if (normalizedOriginal !== normalizedCurrent) {
          const oldDisplayValue = config.oldValue;
          const newDisplayValue = config.newValue ? config.newValue(currentValue) : valueOrFallback(currentValue);

          changedFields.push({
            fieldName: config.label,
            oldValue: oldDisplayValue,
            newValue: newDisplayValue,
          });
        }
      }

      if (changedFields.length > 0) {
        setChangesToConfirm(changedFields);
        setShowConfirmationModal(true);
      } else {
        showNotification("No changes detected.", "info");
        // Keep user in edit mode as requested
      }
    }).catch((errorInfo: any) => {
      const firstErrorField = errorInfo?.errorFields?.[0]?.name;
      if (firstErrorField) {
        form.scrollToField(firstErrorField, { block: "center" });
      }
      showNotification("Please fix the highlighted fields before saving.", "warning");
    });
  };

  const handleUpdateStaff = async () => {
    setIsUpdating(true);
    const allTempData = tempFormData;
    const finalFormState = { ...staff, ...allTempData, ...getCurrentVisibleFormValues() };

    try {
      const payload: any = {};
      for (const config of getVisibleEditableFieldsConfig()) {
        const originalValue = config.getValue(staff);
        let currentValue = finalFormState[config.key];

        if (config.type === "date") {
          currentValue = normalizeDatePayloadValue(currentValue);
        }

        const normalizedOriginal = normalizeComparableValue(originalValue, config.type);
        const normalizedCurrent = normalizeComparableValue(currentValue, config.type);

        if (normalizedOriginal !== normalizedCurrent) {
          if (config.key === "aadhaar") {
            currentValue = unformatAadhaar(currentValue);
          }

          payload[config.key] =
            config.type === "json"
              ? normalizeDependents(currentValue)
              : config.type === "positionHistory"
                ? normalizePositionHistory(currentValue)
                : currentValue;
        }
      }

      if (Object.keys(payload).length > 0) {
        const response: any = await dispatch(updateStaff({ id: staffId!, body: payload })).unwrap();
        const result = response?.data || response;
        if (result?.statusCode < 400) {
          const locationToNavigate = pendingLocation;
          await fetchStaff(); // Re-fetch staff data to get the latest updates
          setTempFormData({}); // Clear temporary data
          setHasUnsavedChanges(false);
          fetchActivityLogs(); // Re-fetch activity logs
          showNotification("Staff details updated successfully.", "success");
          setShowConfirmationModal(false);
          setEditingSection(null);
          setPendingLocation(null);

          if (locationToNavigate) {
            navigate(
              locationToNavigate.pathname +
                (locationToNavigate.search || "")
            );
          }
        } else {
          showNotification(response?.message || "Failed to update staff details.", "error");
        }
      } else {
        showNotification("No changes to update.", "info");
      }
    } catch (error: any) {
      showNotification(error?.message || "Failed to update staff details.", "error");
    } finally {
      setIsUpdating(false);
    }
  };
  

  const renderCompensation = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Section title="Current Annual CTC" className="staff-profile-comp-card">
          <div className="staff-profile-annual-ctc">
            <strong>{mock.compensation.currentAnnualCtc}</strong>
            <span>{mock.compensation.ctcPeriod}</span>
          </div>
          <div className="staff-profile-pay-breakdown">
            {mock.compensation.breakdown.map((item) => (
              <div className="staff-profile-pay-row" key={item.label}>
                <span>{item.label}</span>
                <strong>{item.amount}</strong>
              </div>
            ))}
          </div>
          <p className="staff-profile-muted-line">
            Effective {mock.compensation.effectiveFrom} - Payroll: {mock.compensation.payroll}
          </p>
        </Section>
      </Col>
      <Col xs={24} lg={12}>
        <Section title="Salary Revision History">
          <Table
            pagination={false}
            size="small"
            rowKey="effectiveFrom"
            dataSource={mock.compensation.salaryRevisionHistory}
            columns={[
              { title: "Effective From", dataIndex: "effectiveFrom" },
              { title: "Annual CTC", dataIndex: "annualCtc" },
              {
                title: "% Change",
                dataIndex: "change",
                render: (change: string) => (
                  <span className={change !== "-" ? "staff-profile-positive-change" : ""}>
                    {change}
                  </span>
                ),
              },
              { title: "Reason", dataIndex: "reason" },
            ]}
          />
          <p className="staff-profile-muted-line">
            {mock.compensation.note}
          </p>
        </Section>
      </Col>
    </Row>
  );

  const activeDocument = useMemo(() => {
    return documentTypes.find((dt) => dt.id === activeDocumentCategoryId) || { id: "", documentTypeName: "Select a Document Type" };
  }, [activeDocumentCategoryId, documentTypes]);

  const renderOverview = () => (
    <>
      <Row gutter={[16, 16]} className="staff-profile-metrics">
        {metrics.map((metric) => (
          <Col xs={24} sm={12} lg={6} key={metric.label}>
            <div className="staff-profile-metric-card">
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.note}</small>
            </div>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Section title="Employment Snapshot">
            <FieldGrid fields={employmentSnapshot} />
          </Section>
        </Col>
        <Col xs={24} lg={9}>
        {accessAllowed(myPermissions, allPermissions.STAFF_ACTIVITY_LOGS) && (
          <Section title="Recent Activity">
            <div className="staff-profile-activity-list">
              {recentActivities.map((activity) => (
                <div className="staff-profile-activity" key={`${activity.title}-${activity.date}`}>
                  <span />
                  <div>
                    <strong>{activity.title}</strong>
                    <small>{activity.date}</small>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}
        </Col>
      </Row>
    </>
  );

  const handleSectionEdit = (section: string) => {
    setEditingSection(section);
    const formValues: any = { ...staff };
    formValues.presentAddress = staff?.presentAddress || "";
    formValues.permanentAddress = staff?.permanentAddress || "";
    formValues.dependents = normalizeDependents(parseDependents(staff?.dependents));
    formValues.positionHistory = toPositionHistoryFormValues(parsePositionHistory(staff?.positionHistory));
    if (formValues.doj) formValues.doj = dayjs(formValues.doj);
    if (formValues.contractStartDate) {
      formValues.contractStartDate = dayjs(formValues.contractStartDate);
    }
    if (formValues.contractEndDate) {
      formValues.contractEndDate = dayjs(formValues.contractEndDate);
    }
 if (formValues.dateOfBirth) formValues.dateOfBirth = moment(formValues.dateOfBirth);
    if (formValues.lwd) formValues.lwd = moment(formValues.lwd);
 
    if (formValues.previousSalaryRevisionDate) {
      formValues.previousSalaryRevisionDate = dayjs(formValues.previousSalaryRevisionDate);
    }
    if (formValues.nextSalaryRevisionDate) {
      formValues.nextSalaryRevisionDate = dayjs(formValues.nextSalaryRevisionDate);
    }
    setShowContractFields(!resolveEmploymentModeFlags(staff?.employmentMode).isPermanent);
    setShowVendorField(resolveEmploymentModeFlags(staff?.employmentMode).isVendor);
    // Pre-fetch dynamic dropdown options if parent is already set
    if (formValues.entity) {
      fetchAccountListByEntity(formValues.entity);
    }
    if (formValues.accountId) {
      const projectsForClient = projects.filter(p => p.accountId === formValues.accountId);
      setFilteredProjectList(projectsForClient);
    }
    if (formValues.departmentId) {
      fetchSubDepartmentsList(formValues.departmentId);
    }
    form.setFieldsValue(formValues);
  };

  const handleSectionCancel = () => {
    setEditingSection(null);
    form.resetFields();
  };

  const handleSectionSave = () => {
    onSaveEdit();
  };

  const renderPersonal = () => (
    <>
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
      <Section
          title="Basic Information"          
          canEdit={false}
          onEdit={() => handleSectionEdit("basic")}
          isEditing={editingSection === "basic"}
          onSave={handleSectionSave}
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "basic" || editingSection === "all" ? (
            <Row gutter={16}>
              {editableFieldsConfig.filter(f => ["staffName", "dateOfBirth", "gender", "nationality", "pan", "aadhaar", "bloodGroup", "maritalStatus", "uan"].includes(f.key)).map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={basicInformation} />
          )}
        </Section>
        <Section
          title="Contact Details"
          canEdit={false}
          onEdit={() => handleSectionEdit("contact")}
          isEditing={editingSection === "contact"}
          onSave={handleSectionSave} 
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "contact" || editingSection === "all" ? (
            <Row gutter={16}>
              {editableFieldsConfig.filter(f => ["email", "personalEmail", "mobile", "emergencyContact"].includes(f.key)).map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={contactDetails} />
          )}
        </Section>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
          <Section
            title="Present Address"
            canEdit={false}
            isEditing={editingSection === "presentAddress"}
            onSave={handleSectionSave}
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "presentAddress" || editingSection === "all" ? (
              renderEditableField({ key: "presentAddress", label: "Present Address", type: "textarea" })
            ) : (
              <p className="staff-profile-address">{presentAddress}</p>
            )}
          </Section>
        </Col>
        <Col xs={24} md={12}>
          <Section
            title="Permanent Address"
            canEdit={false}
            isEditing={editingSection === "permanentAddress"}
            onSave={handleSectionSave}
            onCancel={handleCancelClick}
            loading={loading}
          >
            {editingSection === "permanentAddress" || editingSection === "all" ? (
              renderEditableField({ key: "permanentAddress", label: "Permanent Address", type: "textarea" })
            ) : (
              <p className="staff-profile-address">{permanentAddress}</p>
            )}
          </Section>
        </Col>
      </Row>
      <Section
        title="Dependents"
        canEdit={false}
        isEditing={editingSection === "dependents"}
        onSave={handleSectionSave}
        onCancel={handleCancelClick}
        loading={loading}
      >
        {editingSection === "dependents" || editingSection === "all" ? (
          <Form.List name="dependents">
            {(fields, { add, remove }) => (
              <div className="staff-profile-dependents-form">
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={12} key={key} align="middle">
                    <Col xs={24} md={6}>                      
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        label="Name"
                        rules={[
                          { required: true, message: "Please enter Dependent's Name" },
                          textOnlyRule("Dependent's Name"),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "relationship"]}
                        label="Relationship"
                        rules={[
                          { required: true, message: "Please enter Relationship" },
                          textOnlyRule("Relationship"),
                        ]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "dateOfBirth"]}
                        label="Date of Birth"
                        rules={[
                          { required: true, message: "Please enter Date of Birth" },
                          {
                            validator: async (_, value) => {
                              if (value && dayjs(value).isAfter(dayjs(), "day")) {
                                return Promise.reject(new Error("Date of Birth cannot be in the future"));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <DatePicker format="DD-MMM-YYYY" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={20} md={5}>
                      <Form.Item {...restField} name={[name, "coveredInInsurance"]} label="Covered in Insurance">
                        <Select
                          options={[
                            { label: "Yes", value: "Yes" },
                            { label: "No", value: "No" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={4} md={1}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({ coveredInInsurance: "No" })}
                >
                  Add Dependent
                </Button>
              </div>
            )}
          </Form.List>
        ) : (
          <Table
            pagination={false}
            size="small"
            rowKey={(row) => `${row.name}-${row.relationship}`}
            dataSource={displayDependents}
            columns={[
              { title: "Name", dataIndex: "name" },
              { title: "Relationship", dataIndex: "relationship" },
              { title: "Date of Birth", dataIndex: "dateOfBirth", render: (date) => dateOrFallback(date) },
              { title: "Covered in Insurance", dataIndex: "coveredInInsurance" },
            ]}
          />
        )}
      </Section>
      </Form>
    </>
  );

  const renderJob = () => {
    const workDetailFieldOrder = [
      "entity",
      "employeeId",
      "employmentMode",
      "employeeType",
      "designation",
      "role",
      "doj",
      ...(showContractFields ? ["contractStartDate", "contractEndDate"] : []),
      "previousSalaryRevisionDate",
      "nextSalaryRevisionDate",
      "lwd",
      "departmentId",
      "subDepartmentId",
      "lineManagerId",
      "noticePeriod",
      "workLocation",
      "gradeBand",
    ];
    const editableFieldByKey = new Map(
      editableFieldsConfig.map((config) => [config.key, config])
    );
    const workDetailFields = workDetailFieldOrder
      .map((fieldKey) => editableFieldByKey.get(fieldKey))
      .filter(
        (
          config
        ): config is NonNullable<ReturnType<typeof editableFieldByKey.get>> =>
          Boolean(config)
      );

    return (
    <>
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
        <Section
          title="Work Details"
          canEdit={false}
          onEdit={() => handleSectionEdit("work")}
          isEditing={editingSection === "work"}
          onSave={handleSectionSave} // This will trigger the global onSaveEdit
          onCancel={handleCancelClick}
          loading={loading}
        >
          {editingSection === "work" || editingSection === "all" ? (
            <Row gutter={16}>
              {workDetailFields.map(config => (
                <Col span={12} key={config.key}>
                  {renderEditableField(config)}
                </Col>
              ))}
            </Row>
          ) : (
            <FieldGrid fields={visibleWorkDetails} />
          )}
        </Section>
      </Form>
      <Form form={form} layout="vertical" initialValues={staff} onValuesChange={onFormValuesChange}>
      <Section
        title="Position History"
        canEdit={false}
        isEditing={editingSection === "positionHistory"}
        onSave={handleSectionSave}
        onCancel={handleCancelClick}
        loading={loading}
      >
        {editingSection === "positionHistory" || editingSection === "all" ? (
          <Form.List name="positionHistory">
            {(fields, { add, remove }) => (
              <div className="staff-profile-dependents-form">
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={12} key={key} align="middle">
                    <Col xs={24} md={5}>
                      <Form.Item {...restField} name={[name, "effectiveFrom"]} label="Effective From">
                        <DatePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item {...restField} name={[name, "designation"]} label="Designation">
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                      <Form.Item {...restField} name={[name, "department"]} label="Department">
                        <Select
                          placeholder="Select Department"
                          allowClear
                          options={departments.map((department) => ({
                            label: department.departmentName,
                            value: department.departmentName,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={20} md={6}>
                      <Form.Item {...restField} name={[name, "reportingTo"]} label="Reporting To">
                        <Select
                          placeholder="Select Reporting To"
                          allowClear
                          options={lineManagers.map((staffMember) => ({
                            label: staffMember.staffName,
                            value: staffMember.staffName,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={4} md={1}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({})}
                >
                  Add Position
                </Button>
              </div>
            )}
          </Form.List>
        ) : (
          <Table
            pagination={false}
            size="small"
            rowKey={(row) => `${row.effectiveFrom}-${row.designation}-${row.reportingTo}`}
            dataSource={displayPositionHistory}
            columns={[
              { title: "Effective From", dataIndex: "effectiveFrom" },
              { title: "Designation", dataIndex: "designation" },
              { title: "Department", dataIndex: "department" },
              { title: "Reporting To", dataIndex: "reportingTo" },
            ]}
          />
        )}
      </Section>
      </Form>
    </>
    );
  };

  const renderOnboarding = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={13}>
        <Section title="Onboarding Checklist">
          <Tag color="success" className="staff-profile-inline-tag">
            Completed {mock.onboarding.completedOn}
          </Tag>
          <div className="staff-profile-checklist">
            {mock.onboarding.checklist.map((item) => (
              <div className="staff-profile-checklist-item" key={item.title}>
                <CheckCircleFilled />
                <span>{item.title}</span>
                <small>{item.date}</small>
              </div>
            ))}
          </div>
        </Section>
      </Col>
      <Col xs={24} lg={11}>
        <Section title="Offer & Pre-joining">
          <FieldGrid fields={mock.onboarding.offerPreJoining} />
        </Section>
        <Section title="Background Verification">
          <FieldGrid fields={mock.onboarding.backgroundVerification} />
        </Section>
        <Section title="Induction">
          <FieldGrid fields={mock.onboarding.induction} />
        </Section>
      </Col>
    </Row>
  );

  const renderDocuments = () => (
    <Row gutter={[16, 16]} className="staff-profile-documents">
      <Col xs={24} md={7} lg={5}>
        <div className="staff-profile-document-nav">
          {loadingDocumentTypes ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : documentTypes.length > 0 ? (
            documentTypes.map((docType) => (
              <button
                type="button"
                key={docType.id}
                className={docType.id === activeDocument.id ? "active" : ""}
                onClick={() => setActiveDocumentCategoryId(docType.id)}
              >
                <FolderOpenOutlined />
                <span>{docType.documentTypeName}</span>
                <small>{documentCounts[docType.id] || 0}</small>
              </button>
            ))
          ) : ( // Corrected closing parenthesis for the ternary operator
            <Empty description="No document types found" />
          )}
        </div>
      </Col>
      <Col xs={24} md={17} lg={19}>
        <Section title={activeDocument.documentTypeName}> {/* Changed to documentTypeName */}
          { accessAllowed(myPermissions, allPermissions.FILE_UPLOAD_STAFF ) && ( // Assuming a permission for uploading documents */}
            <Button
              size="small"
              icon={<UploadOutlined />}
              className="staff-profile-section-action"
              onClick={() => setShowUploadModal(true)}
            >
              Upload File
            </Button>
          )}
          <Table
            pagination={false}
            size="small"
            rowKey="id" // Assuming each document has a unique 'id'
            dataSource={staffDocuments}
            loading={loadingStaffDocuments}
            columns={[
              {
                title: "File Name",
                dataIndex: "fileName",
                render: (fileName: string) => (
                  <Space>
                    <FileTextOutlined />
                    {fileName}
                  </Space>
                ),
              },
              { title: "Document Type", dataIndex: "documentTypeName" },
              { title: "Document Sub Type", dataIndex: "documentSubTypeName" },
              // API aliases this as `uploadedDate` (documents.service.ts select list).
              { title: "Uploaded On", dataIndex: "uploadedDate", render: (date: string) => formatDate(date, "DD-MMM-YYYY") },
              { title: "Uploaded By", dataIndex: "uploadedBy" },
              {
                title: "Action",
                align: "right",
                render: (_: any, record: any) => (
                  <Space size="small">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      loading={documentActionLoading === record.id}
                      onClick={() => handleDownloadDocument(record)}
                    >
                      Download
                    </Button>
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      loading={documentActionLoading === record.id}
                      onClick={() => handleDeleteDocument(record)}
                    >
                      Delete
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Section>
      </Col>
    </Row>
  );

  const renderLetters = () => (
    <Section title="Letters Issued">
      <Button type="primary" size="small" className="staff-profile-section-action">
        Generate Letter
      </Button>
      <Table
        pagination={false}
        size="small"
        rowKey="referenceNo"
        dataSource={mock.letters.issued}
        columns={[
          {
            title: "Letter",
            dataIndex: "letter",
            render: (letter: string) => (
              <Space>
                <span className="staff-profile-file-icon">
                  <FileTextOutlined />
                </span>
                {letter}
              </Space>
            ),
          },
          { title: "Reference No.", dataIndex: "referenceNo" },
          { title: "Issued On", dataIndex: "issuedOn" },
          { title: "Issued By", dataIndex: "issuedBy" },
          {
            title: "Action",
            align: "right",
            render: () => <Button type="link">Download</Button>,
          },
        ]}
      />
    </Section>
  );


  const renderAssets = () => (
    <Section title="Assets Issued">
      <Button type="primary" size="small" className="staff-profile-section-action">
        Assign Asset
      </Button>
      <Table
        pagination={false}
        size="small"
        rowKey="referenceNo"
        dataSource={mock.assets.issued}
        columns={[
          {
            title: "Asset",
            dataIndex: "asset",
          },
          { title: "Asset Tag.", dataIndex: "assetTag" },
          { title: "Serial No", dataIndex: "serialNo" },
          { title: "Issued On", dataIndex: "issuedOn" },
          { title: "Returned On", dataIndex: "returnOn" },
          {
            title: "status",           
            dataIndex: "status"
            ,
          },
        ]}
      />
    </Section>
  );

  const renderLeaveAndAttendance = () => (
    <>
      <Row gutter={[16, 16]} className="staff-profile-metrics">
        {mock.leave.balance.map((leave) => (
          <Col xs={24} sm={12} lg={6} key={leave.type}>
            <div className="staff-profile-leave-card">
              <div className="staff-profile-leave-card-header">
                <span>{leave.type}</span>
                <strong>
                  {leave.used} / {leave.total}
                </strong>
              </div>
              <Progress
                percent={(leave.used / leave.total) * 100}
                showInfo={false}
                strokeColor="var(--primary)"
              />
            </div>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Section title="Recent Leave Requests">
            <Table
              pagination={false}
              size="small"
              rowKey={(row) => `${row.type}-${row.dates}`}
              dataSource={mock.leave.recentRequests}
              columns={[
                { title: "Type", dataIndex: "type" },
                { title: "Dates", dataIndex: "dates" },
                { title: "Days", dataIndex: "days" },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status: string) => (
                    <Tag color={status === "Pending" ? "warning" : "success"}>
                      {status}
                    </Tag>
                  ),
                },
              ]}
            />
          </Section>
        </Col>
        <Col xs={24} lg={9}>
          <Section title="Attendance Summary — FY 2025-26">
            <div className="staff-profile-field-grid staff-profile-field-grid-dense">
              {mock.leave.attendanceSummary.map((item) => (
                <div
                  className="staff-profile-field"
                  key={item.label}
                  style={{
                    borderBottom: "1px solid var(--border-color)",
                    paddingBottom: "0.75rem",
                  }}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </Section>
        </Col>
      </Row>
    </>
  );

  const renderPerformance = () => (
    <>
      <Section title="Appraisal History">
        <Table
          pagination={false}
          size="small"
          rowKey="cycle"
          dataSource={mock.performance.appraisalHistory}
          columns={[
            { title: "Cycle", dataIndex: "cycle" },
            { title: "Reviewed By", dataIndex: "reviewedBy" },
            {
              title: "Rating",
              dataIndex: "rating",
              render: (rating: string) => (
                <strong className="staff-profile-rating">{rating}</strong>
              ),
            },
            { title: "Remarks", dataIndex: "remarks" },
            { title: "Outcome", dataIndex: "outcome" },
          ]}
        />
      </Section>
      <Section title="Goals — FY 2025 Cycle">
        <div className="staff-profile-goals-grid">
          {mock.performance.goals.map((goal) => (
            <Row align="middle">
              <Col flex="auto">
                <div className="goal-title">{goal.title}</div>
                <div className="goal-description">
                  {goal.description}
                </div>
              </Col>  
              <Col style={{ width: 180 }}>
            <Progress
              percent={goal.progress}
              size="small"
            />
          </Col>      
          </Row>

          ))}
        </div>
      </Section>
    </>
  );
const renderTimeline = () => {
  return (
    <Card
      title={
          <span style={{ fontWeight: 700, fontSize: "18px" }}>
            Employment Timeline
          </span>
        }
      bordered={false}
      className="staff-profile-card"
      
    >
      <div style={{marginLeft :"-600px"}}>
      <Timeline mode="left" style={{marginLeft :"-600px"}}>
  {mock.timeline.map((item, index) => (
    <Timeline.Item
      key={index}
      color="var(--primary)"
      label={<span className="timeline-date">{item.date}</span>}
    >
      <div className="timeline-title">{item.title}</div>
      <div className="timeline-description">{item.description}</div>
    </Timeline.Item>
  ))}
</Timeline>
</div>
    </Card>
  );
};

const Field = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="staff-field">
    <div className="staff-field-label">{label}</div>

    <div className="staff-field-value">{value}</div>
  </div>
);

const renderExit = () => {
  return (
    <Row gutter={[16, 16]}>
      {/* LEFT SIDE */}

      <Col xs={24} lg={12}>

        <Card
          title="Resignation Details"
          className="staff-card"
        >

          <Row gutter={[16, 16]}>

            <Col span={12}>
              <Field
                label="Resignation Date"
                value={mock.exit.resignationDate}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Last Working Day"
                value={mock.exit.lastWorkingDay}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Notice Served"
                value={mock.exit.noticeServed}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Exit Type"
                value={mock.exit.exitType}
              />
            </Col>

            <Col span={24}>
              <Field
                label="Reason For Leaving"
                value={mock.exit.reason}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Rehire Eligible"
                value={mock.exit.rehireEligible}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Exit Interview"
                value={mock.exit.exitInterview}
              />
            </Col>

          </Row>

        </Card>

        <Card
          title="Full & Final Settlement"
          className="staff-card"
          style={{ marginTop: 16 }}
        >

          <Row gutter={[16, 16]}>

            <Col span={12}>
              <Field
                label="Status"
                value={mock.exit.status}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Net Payable"
                value={mock.exit.netPayable}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Leave Encashment"
                value={mock.exit.leaveEncashment}
              />
            </Col>

            <Col span={12}>
              <Field
                label="Gratuity"
                value={mock.exit.gratuity}
              />
            </Col>

          </Row>

        </Card>

      </Col>

      {/* RIGHT SIDE */}

      <Col xs={24} lg={12}>

        <Card
          title="Exit Clearance Checklist"
          className="staff-card"
        >

          <List
            split={false}
            dataSource={mock.exit.checklist}
            renderItem={(item) => (
              <List.Item>

                <Row style={{ width: "100%" }}>

                  <Col flex="auto">

                    <CheckCircleFilled
                      style={{
                        color: "var(--success)",
                        marginRight: 10,
                      }}
                    />

                    {item.title}

                  </Col>

                  <Col>

                    <Typography.Text type="secondary">
                      {item.owner} 
                    </Typography.Text>

                  </Col>

                </Row>

              </List.Item>
            )}
          />

          <Alert
            style={{ marginTop: 20 }}
            type="info"
            showIcon={false}
            message="Relieving & experience letters were issued on 02-Apr-2026 and are available under the Letters tab."
          />

        </Card>

      </Col>

    </Row>
  );
};

  const renderTab = (key: StaffProfileTabKey) => {
    switch (key) {
      case "overview":
        return renderOverview();
      case "personal":
        return renderPersonal();
      case "job":
        return renderJob();
      case "onboarding":
        return renderOnboarding();
      case "documents":
        return renderDocuments();
      case "letters":
        return renderLetters();
      case "compensation":
        return renderCompensation();
      case "assets":
        return renderAssets();     
      case "leave":    
         return renderLeaveAndAttendance();      
      case "performance":
        return renderPerformance();
      case "timeline":
        return renderTimeline();
      case "exit":
        return renderExit();
      default:
        return <Empty />;
    }
  };

  return (
    <div className="staff-profile-page">
      <div className="staff-profile-topbar">
        <Button
          type="link"
          icon={<LeftOutlined />}
          onClick={() => navigate(ROUTE_MANAGE_STAFF)}
        >
          Back to Manage Staff
        </Button>
        {/* <div className="staff-profile-preview-state">
          <span>Preview State</span>
          <button
            type="button"
            className={`staff-profile-preview-state-button staff-profile-preview-state-button-${getStaffStatusColor("ACTIVE")}${!isExitPreview ? " active" : ""}`}
            onClick={() => switchProfileTab("overview")}
          >
            Active
          </button>
          <button
            type="button"
            className={`staff-profile-preview-state-button staff-profile-preview-state-button-${getStaffStatusColor("INACTIVE")}${isExitPreview ? " active" : ""}`}
            onClick={() => switchProfileTab("exit")}
          >
            Exited
          </button>
        </div> */}
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <>
          <div className="staff-profile-header-card">
            <div className="staff-profile-avatar">{initialsFor(staffName)}</div>
            <div className="staff-profile-identity">
              <div className="staff-profile-title-row">
                <h1>{staffName}</h1>
                <Tag color={getStaffStatusColor(status)}>{status}</Tag>
              </div>
              <p>
                {[designation, role].filter((value) => value && value !== "-").join(" - ") || "-"}
              </p>
              <div className="staff-profile-meta">
                <span>Employee ID <strong>{employeeId}</strong></span>
                <span>Department <strong>{department}</strong></span>
                <span>Line Manager <strong>{lineManager}</strong></span>
                <span>Date of Joining <strong>{joiningDate}</strong></span>
                <span>Tenure <strong>{tenure}</strong></span>
                <span>Client <strong>{client}</strong></span>
                <span>Project <strong>{project}</strong></span>
              </div>
            </div>
            {canUpdateStaff && (
              <div className="staff-profile-actions">
                {/* <Button icon={<DownloadOutlined />}>Download Profile</Button> */}
                {editingSection ? (
                  <Space>
                    <Button onClick={handleCancelClick}>Cancel</Button>
                    <Button type="primary" onClick={onSaveEdit} loading={loading} disabled={!hasUnsavedChanges}>
                      Save Details
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}                    
                    onClick={() => handleSectionEdit("all")}
                  >
                    Edit Staff Details
                  </Button>
                )}
              </div>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={(key) => handleTabChange(key as StaffProfileTabKey)}
            className="staff-profile-tabs"
          >
            {profileTabs.map((tab) => (
              <TabPane tab={tab.label} key={tab.key}>
                <div className="staff-profile-tab-panel">
                  {tabLoading && tab.key === activeTab ? (
                    <div className="staff-profile-tab-loader">
                      <Skeleton active paragraph={{ rows: 6 }} />
                    </div>
                  ) : (
                    renderTab(tab.key)
                  )}
                </div>
              </TabPane>
            ))}
          </Tabs>
        </>
      )}

      <Modal
        title="Confirm Changes"
        open={showConfirmationModal}
        onCancel={() => {
          setShowConfirmationModal(false);
          setPendingLocation(null); // Clear pending navigation on cancel
        }}
        footer={[
          <Button key="cancel" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>,
          <Button
            key="update"
            type="primary"
            loading={isUpdating}
            onClick={handleUpdateStaff}
          >
            Update
          </Button>,
        ]}
      >
        <p>Please review the changes before updating:</p>
        <Table
          rowKey="fieldName"
          dataSource={changesToConfirm}
          columns={[
            { title: "Field Name", dataIndex: "fieldName" },
            { title: "Old Value", dataIndex: "oldValue" },
            { title: "New Value", dataIndex: "newValue" },
          ]}
          pagination={false}
        />
      </Modal>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <ExclamationCircleOutlined style={{ fontSize: '22px', color: 'var(--warning)', marginRight: 8 }} />
            Save and Continue
          </div>
        }
        open={showUnsavedChangesModal}
        onCancel={() => setShowUnsavedChangesModal(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setShowUnsavedChangesModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveAndContinue}
          >
            Save & Continue
          </Button>,
        ]}
      >
        <p style={{ textAlign: 'center', marginTop: 8 }}>You have unsaved changes. Do you want to save your changes and continue?</p>
      </Modal>

      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <ExclamationCircleOutlined style={{ fontSize: '22px', color: 'var(--warning)', marginRight: 8 }} />
            Discard Changes
          </div>
        }
        open={showDiscardChangesModal}
        onCancel={() => setShowDiscardChangesModal(false)}
        centered
        footer={[
          <Button key="cancel" onClick={() => setShowDiscardChangesModal(false)}>
            Cancel
          </Button>,
          <Button
            key="discard"
            type="primary"
            danger
            onClick={() => { onCancelEdit(); setShowDiscardChangesModal(false); setEditingSection(null); }}
          >
            Discard Changes
          </Button>,
        ]}
      >
        <p style={{ textAlign: 'center', marginTop: 8 }}>You have unsaved changes. Are you sure you want to discard them?</p>
      </Modal>

      <UploadDocumentModal
        visible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        staffId={staffId!}
        documentTypes={documentTypes}
        onSubmit={handleDocumentUploadSuccess}
      />

    </div>
  );
};

export default StaffProfilePage;